<template>
  <div>
    <el-card shadow="hover">
      <template v-if="!embedded" slot="header">
        <div class="clearfix">
          <span>出库管理</span>
          <span class="muted small" style="margin-left:10px">待出库申请（共 {{ total }} 单）</span>
          <div style="float: right">
            <el-button type="success" size="small" @click="exportCsv">导出CSV</el-button>
            <el-button v-if="$hasPerm('func:outbound:create')" type="primary" size="small" icon="el-icon-plus" @click="openCreate">新建出库</el-button>
          </div>
        </div>
      </template>

      <el-form :inline="true" :model="searchForm" class="mb-4">
        <el-form-item v-if="!embedded" label="客户姓名">
          <el-input v-model="searchForm.customerName" placeholder="请输入" style="width: 160px" @keyup.enter.native="handleSearch"></el-input>
        </el-form-item>
        <el-form-item label="出库类型">
          <el-select v-model="searchForm.outboundType" placeholder="全部" clearable style="width: 160px" @change="handleSearch">
            <el-option v-for="t in outboundTypes" :key="t.id" :label="t.name" :value="t.name"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="存货类型">
          <el-select v-model="searchForm.stockType" placeholder="全部" clearable style="width: 130px" @change="handleSearch">
            <el-option label="普通存货" value="普通存货"></el-option>
            <el-option label="品鉴存货" value="品鉴存货"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="礼品类型">
          <el-select v-model="searchForm.giftTypeId" placeholder="全部" clearable style="width: 140px" @change="handleSearch">
            <el-option v-for="t in allGiftTypeOptions" :key="t.id" :label="t.name" :value="t.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="全部" clearable style="width: 130px" @change="handleSearch">
            <el-option label="已完结" value="completed"></el-option>
            <el-option label="未完结" value="uncompleted"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="出库日期">
          <el-date-picker
            v-model="searchForm.outboundDateRange"
            type="daterange" unlink-panels
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width: 380px"
            @change="handleSearch"></el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
        <el-form-item v-if="embedded" class="embed-actions">
          <el-button type="success" size="small" @click="exportCsv">导出CSV</el-button>
          <el-button v-if="$hasPerm('func:outbound:create')" type="primary" size="small" icon="el-icon-plus" @click="openCreate">新建出库</el-button>
        </el-form-item>
      </el-form>

      <el-table :data="list" stripe size="small" border row-key="id" :expand-row-keys="expandedKeys" @expand-change="onExpandChange">
        <el-table-column type="expand" width="40" :fixed="embedded ? false : 'left'">
          <template slot-scope="props">
            <div class="detail-panel">
              <div class="detail-panel-title"><i class="el-icon-info"></i><span>申请详情</span></div>
              <div class="detail-card-row">
                <div class="detail-card-body cols-4">
                  <div class="dc-cell"><span class="dc-label">申请ID</span><span class="dc-value">{{ props.row.id }}</span></div>
                  <div class="dc-cell"><span class="dc-label">申请单号</span><span class="dc-value">{{ props.row.applicationNo || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">客户姓名</span><span class="dc-value">{{ props.row.customerName || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">出库类型</span>
                    <el-tag :type="outTypeTag(props.row)" :class="outTypeClass(props.row.outboundType)" size="mini">{{ vipCardLabel(props.row) || '-' }}</el-tag>
                  </div>
                  <!-- 仅出库类型为试用时展示：试用类型与核验凭证 -->
                  <template v-if="['产品试用', '试用'].includes(props.row.outboundType || props.row.cardName)">
                    <div class="dc-cell"><span class="dc-label">试用类型</span><span class="dc-value">{{ props.row.applicableTypeName || '-' }}</span></div>
                    <div class="dc-cell"><span class="dc-label">核验方式/凭证</span>
                      <span class="dc-value">
                        <template v-if="props.row.verificationType === 1">
                          <el-tag size="mini" type="success" effect="plain"><i class="el-icon-mobile-phone"></i> 短信验证</el-tag>
                          <span v-if="props.row.verifyPhone" style="color:#409EFF;margin-left:6px;font-size:12px;font-weight:600">({{ props.row.verifyPhone }})</span>
                        </template>
                        <template v-else-if="props.row.verificationType === 2">
                          <el-tag size="mini" type="warning" effect="plain">特殊通道</el-tag>
                          <span v-if="props.row.attachmentUrl" style="margin-left:6px;display:inline-flex;align-items:center">
                            <el-image
                              v-if="isImage(props.row.attachmentUrl)"
                              style="width: 22px; height: 22px; border-radius: 2px; vertical-align: middle;"
                              :src="full(props.row.attachmentUrl)"
                              :preview-src-list="[full(props.row.attachmentUrl)]"
                            ></el-image>
                            <el-button
                              v-else
                              size="mini"
                              type="primary"
                              plain
                              icon="el-icon-download"
                              style="padding: 2px 6px; font-size: 11px; margin-left: 2px;"
                              @click="downloadAttachment(props.row.attachmentUrl)"
                            >
                              下载凭证
                            </el-button>
                          </span>
                          <span v-else class="muted" style="margin-left:4px;font-size:12px">（无凭证）</span>
                        </template>
                        <template v-else-if="props.row.verificationType === 3">
                          <el-tag size="mini" type="info" effect="plain">免核验</el-tag>
                        </template>
                        <template v-else>-</template>
                      </span>
                    </div>
                  </template>
                  <!-- 仅出库类型为礼品/损耗等礼品类时展示：礼品类型 -->
                  <template v-if="isGiftOutboundType(props.row.outboundType || props.row.cardName, props.row) || props.row.applicableTypeName">
                    <div class="dc-cell"><span class="dc-label">礼品类型</span><span class="dc-value">{{ props.row.applicableTypeName || '-' }}</span></div>
                  </template>
                  <div class="dc-cell w4"><span class="dc-label">产品</span><span class="dc-value">{{ prodNamesQty(props.row) || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">数量合计</span><span class="dc-value">{{ props.row.totalQuantity || 0 }}</span></div>
                  <div class="dc-cell"><span class="dc-label">申请时间</span><span class="dc-value">{{ (props.row.outboundDate ? String(props.row.outboundDate).slice(0, 10) : '') || formatDate(props.row.createTime, 10) || '-' }} <el-tag v-if="isHistoryReplenishment(props.row)" type="warning" size="mini" style="margin-left:4px;">历史补录</el-tag></span></div>
                  <div class="dc-cell"><span class="dc-label">提交模式</span>
                    <span class="dc-value">
                      <el-tag v-if="['尊贵卡A','尊贵卡B','尊贵卡C'].includes(props.row.outboundType)" :type="getReplenishTagType(props.row)" size="mini">{{ getReplenishTypeLabel(props.row) }}</el-tag>
                      <span v-else>-</span>
                    </span>
                  </div>
                  <div class="dc-cell"><span class="dc-label">审批时间</span><span class="dc-value">{{ formatDate(props.row.approveTime, 10) || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">出库时间</span><span class="dc-value">{{ [0, 2, 6].includes(Number(props.row.status)) ? '-' : (formatDate(props.row.finishTime, 10) || '-') }}</span></div>
                  <div class="dc-cell w4"><span class="dc-label">生产批次</span><span class="dc-value" style="white-space:pre-line">{{ props.row.batchNos || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">经办人</span><span class="dc-value">{{ props.row.userName || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">库管</span><span class="dc-value">{{ props.row.approverName || '-' }}</span></div>
                  <div class="dc-cell w4"><span class="dc-label">出库明细表</span>
                    <template v-if="agreeList(props.row.agreementFile).length">
                      <div class="report-thumbs">
                        <template v-for="(u, i) in agreeList(props.row.agreementFile)">
                          <el-image v-if="isImage(u)" :key="'img'+i" :src="full(u)" :preview-src-list="imagePreviewList(props.row.agreementFile)" fit="cover" class="report-thumb"></el-image>
                          <a v-else :key="'a'+i" :href="full(u)" target="_blank" rel="noopener" class="link report-file">{{ fileShort(u) }}</a>
                        </template>
                      </div>
                    </template>
                    <span v-else class="muted">未扫描</span>
                  </div>
                  <div class="dc-cell w4">
                    <span class="dc-label">申请备注</span>
                    <span class="dc-value">
                      <!-- 按照需求暂时注释：展开行申请备注不自动拼接历史补录提示
                      <template v-if="isHistoryReplenishment(props.row)">
                        <span>⚠️ 历史补录：{{ historyReplenishmentRemarkNote(props.row) || props.row.remark }}</span>
                        <span v-if="props.row.remark && historyReplenishmentRemarkNote(props.row) && props.row.remark !== historyReplenishmentRemarkNote(props.row)" style="margin-left:8px;color:#873800;font-weight:normal;">（手填备注：{{ props.row.remark }}）</span>
                      </template>
                      <template v-else>
                        {{ props.row.remark || '-' }}
                      </template>
                      -->
                      {{ props.row.remark || '-' }}
                    </span>
                  </div>
                  <div class="dc-cell w4"><span class="dc-label">审核备注</span><span class="dc-value">
                    <template v-if="props.row.approveRemark">
                      <span v-if="proxyPrefix(props.row.approveRemark)" style="color:#E62412;font-weight:700">{{ proxyPrefix(props.row.approveRemark) }}</span>{{ proxyBody(props.row.approveRemark) || (proxyPrefix(props.row.approveRemark) ? '' : props.row.approveRemark) }}
                    </template>
                    <template v-else>-</template>
                  </span></div>
                  <div class="dc-cell w4"><span class="dc-label">出库备注</span><span class="dc-value">{{ props.row.outboundRemark || '-' }}</span></div>
                </div>
              </div>
              <!-- 变更记录：仅新出库类型 -->
              <div v-if="props.row.outboundType === '新出库'" style="margin-top:14px">
                <div class="detail-panel-title"><i class="el-icon-time"></i><span>欠款变更记录</span></div>
                <div class="detail-card-row" style="padding:12px 16px">
                  <el-timeline v-if="(changeLogsMap[props.row.id] || []).length">
                    <el-timeline-item v-for="log in changeLogsMap[props.row.id]" :key="log.id"
                                      :timestamp="formatDate(log.createTime)" placement="top" size="normal"
                                      :type="log.action === '欠款修改' ? 'warning' : 'primary'">
                      <div style="font-size:13px">
                        <el-tag size="mini" :type="log.action === '欠款修改' ? 'warning' : 'primary'">{{ log.action }}</el-tag>
                        <span style="margin-left:8px">{{ debtLogVal(log.oldValue) }} → <b style="color:#f56c6c">{{ debtLogVal(log.newValue) }}</b></span>
                        <span v-if="log.operator" style="margin-left:10px;color:#909399">操作人：{{ log.operator }}</span>
                      </div>
                    </el-timeline-item>
                  </el-timeline>
                  <div v-else class="muted">暂无欠款变更记录</div>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column type="index" label="序号" width="60" align="center" :index="indexMethod"></el-table-column>
        <el-table-column v-if="!embedded" prop="applicationNo" label="申请单号" width="200" show-overflow-tooltip></el-table-column>
        <el-table-column label="出库类型" min-width="150">
          <template slot-scope="s">
            <el-tag :type="outTypeTag(s.row)" :class="outTypeClass(s.row.outboundType)" size="small">{{ vipCardLabel(s.row) || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="产品" min-width="220" show-overflow-tooltip>
          <!-- 客户详情页内嵌时列表只显产品名不带数量（展开行仍带）；独立页照常带数量 -->
          <template slot-scope="s">{{ (embedded ? prodNames(s.row) : prodNamesQty(s.row)) || '-' }}</template>
        </el-table-column>
        <el-table-column label="数量合计" width="90" align="center">
          <template slot-scope="s">{{ s.row.totalQuantity || 0 }}</template>
        </el-table-column>
        <el-table-column label="审批时间" width="110">
          <template slot-scope="s">{{ formatDate(s.row.approveTime, 10) || '-' }}</template>
        </el-table-column>
        <el-table-column label="出库时间" width="110">
          <template slot-scope="s">{{ [0, 2, 6].includes(Number(s.row.status)) ? '-' : (formatDate(s.row.finishTime, 10) || '-') }}</template>
        </el-table-column>
        <el-table-column label="生产批次" min-width="140" show-overflow-tooltip>
          <template slot-scope="s">{{ s.row.batchNos || '-' }}</template>
        </el-table-column>
        <el-table-column label="经办人" width="100">
          <template slot-scope="s">{{ s.row.userName || '-' }}</template>
        </el-table-column>
        <el-table-column label="库管" width="100">
          <template slot-scope="s">{{ s.row.approverName || '-' }}</template>
        </el-table-column>
        <el-table-column label="状态" min-width="240">
          <template slot-scope="s">
            <!-- 新出库 + 已完结/未完结 → tag 可点击修改欠款（=0→已完结，>0→未完结）；需"修改新出库完结状态"权限 -->
            <el-tag v-if="$hasPerm('func:outbound:newout-status') && s.row.outboundType === '新出库' && (Number(s.row.status) === 3 || Number(s.row.status) === 4)"
                    :type="statusTag(s.row.outboundType, s.row.status)"
                    :effect="s.row.outboundType === '新出库' && Number(s.row.status) === 4 ? 'dark' : 'light'"
                    :class="{ 'newout-uncompleted-tag': s.row.outboundType === '新出库' && Number(s.row.status) === 4 }"
                    size="small" style="cursor:pointer"
                    @click="changeDebt(s.row)">
              {{ statusLabel(s.row.outboundType, s.row.status) }} <i class="el-icon-edit el-icon--right"></i>
            </el-tag>
            <el-tag v-else :type="statusTag(s.row.outboundType, s.row.status)"
                    :effect="s.row.outboundType === '新出库' && Number(s.row.status) === 4 ? 'dark' : 'light'"
                    :class="{ 'newout-uncompleted-tag': s.row.outboundType === '新出库' && Number(s.row.status) === 4 }"
                    size="small">{{ statusLabel(s.row.outboundType, s.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="id" label="申请ID" width="80"></el-table-column>
        <el-table-column label="操作" width="180" :fixed="embedded ? false : 'right'" align="center">
          <template slot-scope="s">
            <el-button v-if="s.row.status === 5" size="mini" type="success" @click="openDispatch(s.row)">出库</el-button>
            <el-button v-else size="mini" type="info" disabled>已出库</el-button>
            <el-button v-if="$hasPerm('func:outbound:delete')" size="mini" type="danger" plain icon="el-icon-delete" @click="handleDelete(s.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-dialog title="新建出库" :visible.sync="createVisible" width="880px" top="6vh" :close-on-click-modal="false" :close-on-press-escape="false">
        <el-form :model="createForm" :rules="createRules" ref="createForm" label-width="100px" size="small">
          <el-row :gutter="20">
            <el-col :span="24"><el-form-item label="客户姓名">
              <el-input :value="embedded && embeddedCustomer ? embeddedCustomer.name : (createForm.customerName || '-')" disabled></el-input>
            </el-form-item></el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="createForm.outboundType === '送存货' ? 8 : 12"><el-form-item label="类型" prop="categoryId">
              <el-select v-model="createForm.categoryId" placeholder="选择商品分类" style="width:100%" @change="onCreateCategoryChange">
                <el-option v-for="c in categoryList" :key="c.id" :label="c.name" :value="c.id"></el-option>
              </el-select>
            </el-form-item></el-col>
            <el-col :span="createForm.outboundType === '送存货' ? 8 : 12"><el-form-item label="出库类型" prop="outboundType">
              <el-select v-model="createForm.outboundType" placeholder="先选类型" style="width:100%" :disabled="!createForm.categoryId">
                <el-option v-for="t in outboundTypeOpts" :key="t.id" :label="t.name" :value="t.name"></el-option>
              </el-select>
            </el-form-item></el-col>
            <el-col v-if="createForm.outboundType === '送存货'" :span="8"><el-form-item label="存货类型" class="is-required">
              <el-select v-model="createForm.stockType" placeholder="选择存货类型" style="width:100%" @change="onStockTypeChange">
                <el-option label="普通存货" value="普通存货"></el-option>
                <el-option label="品鉴存货" value="品鉴存货"></el-option>
              </el-select>
            </el-form-item></el-col>
          </el-row>
          <!-- 礼品类型选择（仅当出库类型为礼品时展开） -->
          <template v-if="isCreateGiftOutbound">
            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item label="礼品类型" class="is-required">
                  <el-select
                    v-model="createForm.applicableTypeId"
                    placeholder="请选择礼品类型"
                    style="width:100%"
                    @change="onCreateGiftTypeChange"
                    :disabled="giftTypeOptions.length <= 1"
                  >
                    <el-option
                      v-for="t in giftTypeOptions"
                      :key="t.id"
                      :label="t.name"
                      :value="t.id"
                    >
                      <span style="float:left">{{ t.name }}</span>
                      <span v-if="t.customerCategoryName" style="float:right;color:#8492a6;font-size:12px">{{ t.customerCategoryName }}</span>
                    </el-option>
                  </el-select>
                  <div v-if="giftTypeOptions.length === 0" style="color:#F56C6C;font-size:12px;margin-top:4px;">
                    <i class="el-icon-warning"></i> 该客户在礼品名单中未配置任何礼品分类权限，无法申请礼品。
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </template>
          <!-- 试用类型及核验机制（仅当出库类型为产品试用/试用时展开） -->
          <template v-if="isCreateTrialOutbound">
            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item label="试用类型" class="is-required">
                  <el-select v-model="createForm.applicableTypeId" placeholder="请选择试用类型" style="width:100%" @change="onCreateTrialTypeChange">
                    <el-option v-for="t in trialTypeOptions" :key="t.id" :label="t.name" :value="t.id">
                      <span style="float:left">{{ t.name }}</span>
                      <span v-if="t.needVerification === 1" style="float:right;color:#E6A23C;font-size:12px">需核验</span>
                      <span v-else style="float:right;color:#909399;font-size:12px">免核验</span>
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>

            <!-- 核验方式：仅当试用类型设置了 needVerification === 1 时显示 -->
            <el-row v-if="currentCreateTrialType && currentCreateTrialType.needVerification === 1" :gutter="20">
              <el-col :span="24">
                <el-form-item label="核验方式" class="is-required">
                  <el-radio-group v-model="createForm.verificationType">
                    <el-radio :label="1">短信验证</el-radio>
                    <el-radio :label="2">特殊通道（凭证附件）</el-radio>
                  </el-radio-group>
                </el-form-item>
              </el-col>
            </el-row>

            <!-- 短信验证 -->
            <template v-if="currentCreateTrialType && currentCreateTrialType.needVerification === 1 && createForm.verificationType === 1">
              <el-row :gutter="20">
                <el-col :span="15">
                  <el-form-item label="核验手机号" class="is-required">
                    <el-input v-model="createForm.verifyPhone" :disabled="isCreateTrialPhoneLocked" placeholder="请输入11位手机号码"></el-input>
                    <span v-if="isCreateTrialPhoneLocked" style="color:#909399;font-size:12px;display:block;margin-top:2px;">💡 已锁定客户档案预留手机号</span>
                  </el-form-item>
                </el-col>
                <el-col :span="9">
                  <el-button
                    type="primary"
                    plain
                    size="small"
                    style="width:100%;"
                    :disabled="smsCountdown > 0 || !createForm.verifyPhone"
                    @click="sendCreateSmsCode"
                  >
                    {{ smsCountdown > 0 ? `${smsCountdown}s后重新获取` : '获取验证码' }}
                  </el-button>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="24">
                  <el-form-item label="短信验证码" class="is-required">
                    <el-input v-model="createForm.smsCode" placeholder="请输入6位短信验证码" maxlength="6"></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
            </template>

            <!-- 特殊通道附件 -->
            <template v-if="currentCreateTrialType && currentCreateTrialType.needVerification === 1 && createForm.verificationType === 2">
              <el-row :gutter="20">
                <el-col :span="24">
                  <el-form-item label="凭证附件" class="is-required">
                    <el-upload
                      :action="uploadUrl"
                      :http-request="customUploadCreateVoucher"
                      :show-file-list="false"
                      :before-upload="beforeCreateVoucherUpload"
                    >
                      <el-button size="small" type="primary" plain icon="el-icon-upload2">上传凭证文件</el-button>
                      <span class="muted small" style="margin-left:10px;font-size:12px;">支持图片、PDF、Word、Excel等格式</span>
                    </el-upload>
                    <div v-if="createForm.attachmentUrl" style="margin-top:8px;display:flex;align-items:center;background:#f5f7fa;padding:6px 12px;border-radius:4px;border:1px solid #e4e7ed;">
                      <i class="el-icon-document" style="font-size:16px;color:#409EFF;margin-right:8px;"></i>
                      <span style="flex:1;font-size:13px;color:#606266;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ createForm.attachmentName || '凭证附件' }}</span>
                      <el-button size="mini" type="text" style="color:#409EFF;margin-right:8px;" @click="previewOrDownload(createForm.attachmentUrl)">预览/下载</el-button>
                      <el-button size="mini" type="text" style="color:#F56C6C;" icon="el-icon-delete" @click="removeCreateVoucher">删除</el-button>
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
            </template>
          </template>
          <!-- 多张同类卡时让用户选具体哪张（仅 embedded + 尊贵卡 A/B/C + ≥2 张） -->
          <el-row v-if="embedded && ['尊贵卡A','尊贵卡B','尊贵卡C'].includes(createForm.outboundType) && vipCardOptions.length >= 2" :gutter="20">
            <el-col :span="24"><el-form-item label="选择卡" class="is-required">
              <el-select v-model="createForm.orderId" placeholder="请选择" style="width:100%" @change="onCreateVipCardChange">
                <el-option v-for="c in vipCardOptions" :key="c.id" :label="c.label" :value="c.id"></el-option>
              </el-select>
            </el-form-item></el-col>
          </el-row>
          <!-- 尊贵卡A/B/C：当前服务年度 -->
          <el-row v-if="embedded && ['尊贵卡A','尊贵卡B','尊贵卡C'].includes(createForm.outboundType) && createForm.orderId && getSelectedCardServiceYearWindow()" :gutter="20">
            <el-col :span="24"><el-form-item label="当前服务年度">
              <el-input :value="getSelectedCardServiceYearWindow()" disabled style="font-weight:600;color:#409EFF;"></el-input>
            </el-form-item></el-col>
          </el-row>
          <!-- 尊贵卡B/C：服务费到期时间 -->
          <el-row v-if="embedded && ['尊贵卡B','尊贵卡C'].includes(createForm.outboundType) && createForm.orderId" :gutter="20">
            <el-col :span="24"><el-form-item label="服务费到期时间">
              <el-input :value="vipDialogServiceTerm" disabled :style="serviceFeeOverdue ? 'color:#f56c6c' : ''"></el-input>
            </el-form-item></el-col>
          </el-row>
          <!-- 尊贵卡B 专属：剩余额度 + 实际剩余额度 -->
          <el-row v-if="embedded && createForm.outboundType === '尊贵卡B' && createForm.orderId" :gutter="20">
            <el-col :span="12"><el-form-item label="剩余额度">
              <el-input :value="vipDialogQuota" disabled>
                <template slot="prepend">¥</template>
              </el-input>
            </el-form-item></el-col>
            <el-col :span="12"><el-form-item label="实际剩余额度">
              <el-input :value="vipDialogActualQuota" disabled style="color:#67C23A;font-weight:600;">
                <template slot="prepend">¥</template>
              </el-input>
            </el-form-item></el-col>
          </el-row>
          <el-row v-if="serviceFeeWarning && ['尊贵卡B','尊贵卡C'].includes(createForm.outboundType) && createForm.orderId" :gutter="20">
            <el-col :span="24">
              <div style="margin:-8px 0 14px 100px;color:#f56c6c;font-size:12px;">{{ serviceFeeWarning }}</div>
            </el-col>
          </el-row>
          <el-row v-if="['尊贵卡A','尊贵卡B','尊贵卡C'].includes(createForm.outboundType)" :gutter="20">
            <el-col :span="24">
              <el-form-item label="提交模式" class="is-required">
                <el-radio-group v-model="createForm.replenishType" @change="onReplenishTypeChange">
                  <el-radio v-if="$hasPerm('func:pickup:replenish-cross-year')" label="cross_year">跨年度补录</el-radio>
                  <el-radio v-if="['尊贵卡B','尊贵卡C'].includes(createForm.outboundType) && $hasPerm('func:pickup:replenish-in-year')" label="in_year">本年度内补录</el-radio>
                  <el-radio label="normal">{{ createForm.outboundType === '尊贵卡A' ? '本年度提交' : '正常提交' }}</el-radio>
                </el-radio-group>
                <div class="muted small" style="margin-top:4px;color:#E6A23C;font-size:12px;line-height:1.5;">
                  <template v-if="createForm.outboundType === '尊贵卡A'">
                    <template v-if="createForm.replenishType === 'cross_year'">跨年度补录不扣除本年度剩余可领取数量</template>
                    <template v-else>本年度提交扣除本年度的剩余可领取数量</template>
                  </template>
                  <template v-else>
                    <template v-if="createForm.replenishType === 'cross_year'">跨年度补录不扣除本年度应该剩余和实际剩余（或额度）</template>
                    <template v-else-if="createForm.replenishType === 'in_year'">本年度内补录只扣除应该剩余，不扣除实际剩余</template>
                    <template v-else>正常提交扣除本年度应该剩余+实际剩余</template>
                  </template>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24"><el-form-item label="出库日期" prop="createTime">
              <div style="display:flex;align-items:center;max-width:540px;">
                <el-date-picker
                  v-model="formDatePart"
                  type="date"
                  value-format="yyyy-MM-dd"
                  placeholder="选择日期"
                  :picker-options="pickerOptions"
                  :disabled="isAppTimeDisabled"
                  popper-class="sales-order-date-popper"
                  :style="['尊贵卡B','尊贵卡C'].includes(createForm.outboundType) ? 'width:50%' : 'width:100%'"
                  @change="syncOutboundDateTime">
                </el-date-picker>
                <template v-if="['尊贵卡B','尊贵卡C'].includes(createForm.outboundType)">
                  <el-time-picker
                    v-model="formTimePart"
                    format="HH:mm"
                    value-format="HH:mm"
                    placeholder="选择时间(可直接输入)"
                    :picker-options="timePickerOptions"
                    :disabled="isAppTimeDisabled"
                    popper-class="sales-order-date-popper"
                    style="width:36%;margin-left:2%"
                    @change="syncOutboundDateTime">
                  </el-time-picker>
                  <el-button size="mini" type="primary" plain :disabled="isAppTimeDisabled" style="width:12%;margin-left:2%;padding:6px 0;" @click="setFormToNow">此刻</el-button>
                </template>
              </div>
            </el-form-item></el-col>
          </el-row>
          <!-- 按照需求暂时注释：弹窗内不再弹出旧的自动文本提示条幅
          <el-row v-if="['尊贵卡A','尊贵卡B','尊贵卡C'].includes(createForm.outboundType) && historyReplenishmentHintText" :gutter="20">
            <el-col :span="24">
              <div class="history-replenish-dialog-banner">
                <i class="el-icon-warning-outline" style="font-size:16px;"></i>
                <div><strong>历史补录提示</strong>：{{ historyReplenishmentHintText }}</div>
              </div>
            </el-col>
          </el-row>
          -->
          <el-row :gutter="20">
            <el-col :span="12"><el-form-item :label="isCreateGiftOutbound ? '礼品名称' : '产品名称'" prop="productId">
              <el-select
                v-model="createForm.productOptKey"
                filterable
                :placeholder="isCreateGiftOutbound && !createForm.applicableTypeId ? '请先选择礼品类型' : ((embedded && archivePrefix) ? '选择产品(明细内带前缀,明细外无前缀)' : (createForm.categoryId ? (isCreateGiftOutbound ? '选择礼品' : '选择产品') : '先选类型'))"
                style="width:100%"
                :disabled="(!embedded ? !createForm.categoryId : !createForm.outboundType) || (isCreateGiftOutbound && !createForm.applicableTypeId)"
                @change="onCreateProductChange">
                <!-- label 用裸名（决定选中后输入框/列表显示，不带后缀）；下拉面板里用插槽显示带"（剩余存货：N）"的完整文字 -->
                <el-option v-for="p in createProductOpts" :key="p.optKey" :label="p.name" :value="p.optKey">{{ formatProductLabel(p) }}</el-option>
              </el-select>
            </el-form-item></el-col>
            <el-col :span="12"><el-form-item label="数量" prop="quantity">
              <el-input-number v-model="createForm.quantity" :min="1" controls-position="right" style="width:100%"></el-input-number>
            </el-form-item></el-col>
          </el-row>
          <el-row :gutter="20" v-if="!isCreateGiftCategory">
            <el-col :span="24"><el-form-item label="生产批次">
              <div v-for="(s, si) in createForm.batchSplits" :key="si" style="display:flex;gap:8px;align-items:center;margin-bottom:6px">
                <el-select v-model="s.batchNo" filterable allow-create default-first-option clearable
                           :placeholder="batchOpts.length ? '选择批次' : '该产品暂无入库批次'" size="small" style="flex:1">
                  <el-option v-for="b in batchOpts" :key="b.batchNo"
                             :label="b.batchNo + '（剩 ' + b.remainingQuantity + '）'"
                             :value="b.batchNo"
                             :disabled="Number(b.remainingQuantity || 0) <= 0"></el-option>
                </el-select>
                <el-input-number v-model="s.qty" :min="0" :precision="0" controls-position="right" size="small" style="width:140px" placeholder="数量"></el-input-number>
                <el-button v-if="createForm.batchSplits.length > 1" size="mini" type="text" icon="el-icon-delete" style="color:#f56c6c" @click="removeCreateSplit(si)">删除</el-button>
              </div>
              <el-button class="white-text-btn" size="mini" type="primary" plain icon="el-icon-plus" @click="addCreateSplit">添加批次</el-button>
              <span class="muted small" style="margin-left:10px;color:#909399;font-size:12px;">合计需 = 数量 {{ createForm.quantity }}</span>
            </el-form-item></el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12"><el-form-item label="经办人" prop="operator">
              <el-input v-model="createForm.operator" placeholder="请输入经办人"></el-input>
            </el-form-item></el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12"><el-form-item label="库管" prop="warehouseManager">
              <el-select v-model="createForm.warehouseManager" filterable clearable placeholder="选择库管（行政管理员）" style="width:100%">
                <el-option v-for="u in adminList" :key="u.id" :label="u.name || u.username" :value="u.name || u.username"></el-option>
              </el-select>
            </el-form-item></el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24"><el-form-item label="出库备注">
              <el-input v-model="createForm.outboundRemark" type="textarea" :rows="2" maxlength="200" show-word-limit placeholder="请输入出库备注（可选）"></el-input>
            </el-form-item></el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24"><el-form-item label="出库明细表">
              <el-button size="mini" type="primary" icon="el-icon-camera" @click="openScan(null)">出库明细表</el-button>
              <div class="report-thumbs form-report-thumbs" v-if="agreeList(createForm.agreementFile).length">
                <div v-for="(u, i) in agreeList(createForm.agreementFile)" :key="i" class="report-thumb-item">
                  <el-image v-if="isImage(u)" :src="full(u)" :preview-src-list="imagePreviewList(createForm.agreementFile)" fit="cover" class="report-thumb"></el-image>
                  <a v-else :href="full(u)" target="_blank" rel="noopener" class="link small report-file">{{ fileShort(u) }}</a>
                  <i class="el-icon-close report-thumb-close" @click="rmAgree(i)"></i>
                </div>
              </div>
              <span v-else class="muted small" style="margin-left:8px">未扫描（可选）</span>
            </el-form-item></el-col>
          </el-row>
        </el-form>
        <div slot="footer">
          <el-button size="small" @click="createVisible = false">取消</el-button>
          <el-button size="small" type="primary" @click="confirmCreate" :loading="creating">保存</el-button>
        </div>
      </el-dialog>

      <gaopaiyi-scanner :visible.sync="scanVisible" biz-type="agreement" @uploaded="onAgreementScanned"></gaopaiyi-scanner>
      <agreement-change-log-drawer :visible.sync="agrLogVisible" biz-type="OUTBOUND" :biz-id="agrLogId" title="客户出库出库明细表变更记录"></agreement-change-log-drawer>

      <div class="mt-4">
        <el-pagination
          @size-change="s => { pageSize = s; loadList() }"
          @current-change="p => { currentPage = p; loadList() }"
          :current-page="currentPage" :page-sizes="[10, 20, 50]" :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper" :total="total"></el-pagination>
      </div>
    </el-card>

    <!-- 销售订单提交确认：二选一切换 已完结(status=3) / 未完结(status=4) -->
    <el-dialog title="销售订单提交确认" :visible.sync="debtDialog.visible" width="440px" append-to-body :close-on-click-modal="false" :close-on-press-escape="false">
      <div style="font-size:16px;line-height:1.8;padding:8px 4px;">
        是否确认已提交销售订单？<br/>
        <span class="muted" style="font-size:15px;">已提交销售订单 → 点「已完结」</span><br/>
        <span class="muted" style="font-size:15px;">尚未提交销售订单 → 点「未完结」</span>
      </div>
      <div slot="footer">
        <el-button size="small" @click="debtDialog.visible = false">取消</el-button>
        <el-button size="small" type="danger" @click="submitDebtStatus(4)">未完结</el-button>
        <el-button size="small" type="success" @click="submitDebtStatus(3)">已完结</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import GaopaiyiScanner from '../components/GaopaiyiScanner.vue'
import AgreementChangeLogDrawer from '../components/AgreementChangeLogDrawer.vue'

export default {
  name: 'OutboundManagement',
  components: { GaopaiyiScanner, AgreementChangeLogDrawer },
  props: {
    embeddedCustomer: { type: Object, default: null }
  },
  data() {
    return {
      debtDialog: { visible: false, row: null },
      expandedKeys: [],
      scanVisible: false,
      scanTargetId: null,
      agrLogVisible: false,
      agrLogId: null,
      changeLogsMap: {},
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      searchForm: { customerName: '', outboundType: '', stockType: '', giftTypeId: '', outboundDateRange: null, status: '' },
      outboundTypes: [],
      dispatchVisible: false,
      dispatching: false,
      dispatchForm: { id: null, applicationNo: '', operator: '', warehouseManager: '' },
      // 新建出库
      createVisible: false,
      creating: false,
      categoryList: [],
      allProducts: [],
      createCustomerInner: [],
      outboundTypeOpts: [],
      adminList: [],
      userListAll: [],
      batchOpts: [],
      vipCardOptions: [],
      vipDialogQuota: '',
      vipDialogActualQuota: '',
      vipDialogServiceTerm: '',
      songCunHuoMinDate: '',
      serviceFeeOverdue: false,
      serviceFeeWarning: '',
      bufferValue: 10,
      bufferUnit: 'day',
      formDatePart: '',
      formTimePart: '',
      trialTypeOptions: [],
      giftTypeOptions: [],
      allGiftTypeOptions: [],
      smsCountdown: 0,
      smsTimer: null,
      createForm: this.emptyCreateForm(),
      createRules: {
        createTime: [{ required: true, message: '请选择出库日期', trigger: 'change' }],
        categoryId: [{ required: true, message: '请选择类型', trigger: 'change' }],
        outboundType: [{ required: true, message: '请选择出库类型', trigger: 'change' }],
        productId: [{ required: true, message: '请选择产品', trigger: 'change' }],
        quantity: [{ required: true, message: '请输入数量', trigger: 'blur' }],
        operator: [{ required: true, message: '请输入经办人', trigger: 'blur' }],
        warehouseManager: [{ required: true, message: '请输入库管', trigger: 'blur' }]
      }
    }
  },
  computed: {
    embedded() { return !!(this.embeddedCustomer && this.embeddedCustomer.id) },
    isCreateTrialOutbound() { return ['试用', '产品试用'].includes(this.createForm.outboundType) },
    isCreateTrialPhoneLocked() {
      if (!this.isCreateTrialOutbound) return false
      let raw = ''
      if (this.embedded && this.embeddedCustomer) {
        raw = (this.embeddedCustomer.contactPhone || this.embeddedCustomer.phone || '').trim()
      }
      return !!(raw && /^1[3-9]\d{9}$/.test(raw) && !raw.startsWith('0000'))
    },
    currentCreateTrialType() { return (this.trialTypeOptions || []).find(t => t.id === this.createForm.applicableTypeId) || null },
    isCreateGiftOutbound() { return this.createForm.outboundType === '礼品' || this.isCreateGiftCategory },
    currentCreateGiftType() { return (this.giftTypeOptions || []).find(t => t.id === this.createForm.applicableTypeId) || null },
    uploadHeaders() {
      const t = sessionStorage.getItem('token')
      return t ? { Authorization: 'Bearer ' + t } : {}
    },
    uploadUrl() { return (axios.defaults.baseURL || '') + '/file/upload' },
    archivePrefix() {
      const t = this.createForm.outboundType
      if (t === '送存货') return '存货-'
      if (t === '尊贵卡A' || t === '尊贵卡B' || t === '尊贵卡C') return '尊贵卡-'
      return ''
    },
    createProductOpts() {
      // 嵌入模式(锁定客户) + 出库类型在 送存货/尊贵卡A/尊贵卡C：仅展示客户名下已购卡/存货明细产品（不混入公司公共库存）
      // 尊贵卡B：只显示公司库存的产品（不在下拉中按单列明细内剩余可领取）
      if (!this.createForm.categoryId) return []
      const sameCat = (p) => p && p.categoryId === this.createForm.categoryId
      if (this.embedded && this.archivePrefix && this.allProducts.length && ['送存货', '尊贵卡A', '尊贵卡C'].includes(this.createForm.outboundType)) {
        const innerArr = this.createCustomerInner || []
        return innerArr
          .map(it => ({ ...it, _p: this.allProducts.find(x => x.id === it.productId) }))
          .filter(it => sameCat(it._p))
          .map(it => ({
            optKey: it.productId + '_in',
            id: it.productId,
            name: (this.archivePrefix && !String(it.productName || '').startsWith(this.archivePrefix)) ? (this.archivePrefix + it.productName) : it.productName,
            remaining: it.remaining != null ? it.remaining : 0,
            shouldRemaining: it.shouldRemaining != null ? it.shouldRemaining : (it.remaining != null ? it.remaining : 0),
            actualRemaining: it.actualRemaining != null ? it.actualRemaining : (it.shouldRemaining != null ? it.shouldRemaining : (it.remaining != null ? it.remaining : 0)),
            _inner: true
          }))
      }
      let prods = this.allProducts.filter(sameCat)
      if (this.isCreateTrialOutbound && this.createForm.applicableTypeId) {
        prods = prods.filter(p => p.applicableTypeId === this.createForm.applicableTypeId)
      }
      if (this.isCreateGiftOutbound) {
        if (!this.createForm.applicableTypeId) return []
        const applyTypeId = this.createForm.applicableTypeId
        prods = prods.filter(p => {
          if (p.giftTypeIds && p.giftTypeIds.length) {
            return p.giftTypeIds.includes(applyTypeId)
          }
          if (p.giftTypeList && p.giftTypeList.length) {
            return p.giftTypeList.some(t => t.giftTypeId === applyTypeId)
          }
          return false
        })
      }
      return prods.map(p => ({ optKey: p.id + '_out', id: p.id, name: p.name, stock: p.stock || 0, _inner: false }))
    },
    isCreateGiftCategory() {
      const c = this.categoryList.find(x => x.id === this.createForm.categoryId)
      return !!(c && ['Gift', '礼品'].includes(c.name))
    },
    minOutboundDateStr() {
      const card = this.createForm.outboundType
      if (!card || !['尊贵卡A', '尊贵卡B', '尊贵卡C', '送存货'].includes(card)) {
        return ''
      }
      if (['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(card)) {
        let sel = null
        if (this.createForm.orderId) {
          sel = (this.vipCardOptions || []).find(c => c.id === this.createForm.orderId)
        } else if ((this.vipCardOptions || []).length > 0) {
          sel = this.vipCardOptions[0]
        }
        if (sel) {
          const start = String(sel.serviceFeeStartDate || '').slice(0, 10)
          if (start) return start
        }
        let minD = ''
        ;(this.vipCardOptions || []).forEach(c => {
          const dt = String(c.serviceFeeStartDate || '').slice(0, 10)
          if (dt && (!minD || dt < minD)) minD = dt
        })
        if (minD) return minD
      } else if (card === '送存货') {
        if (this.songCunHuoMinDate) return this.songCunHuoMinDate
      }
      return ''
    },
    isAppTimeDisabled() {
      const card = this.createForm.outboundType
      if (!['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(card)) return false
      let selCard = null
      if (this.createForm.orderId) {
        selCard = (this.vipCardOptions || []).find(c => c.id === this.createForm.orderId)
      } else if ((this.vipCardOptions || []).length > 0) {
        selCard = this.vipCardOptions[0]
      }
      if (!selCard) return false
      const contractStartStr = String(selCard.serviceFeeStartDate || '').slice(0, 10)
      if (!contractStartStr) return false
      const pad = n => String(n).padStart(2, '0')
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`

      // 规则 1：未来卡（today < 合同服务起始日）→ 无论哪种模式，全部置灰！
      if (todayStr < contractStartStr) {
        return true
      }
      // 计算当前服务年度起点
      const winStartStr = this.getCardWindowStartStr(selCard) || contractStartStr
      const replenish = this.createForm.replenishType || 'normal'

      // 规则 2：【跨年度补录】模式下，若 contractStartStr == winStartStr（处于第1服务年度）→ 全部置灰！
      if (replenish === 'cross_year') {
        if (contractStartStr === winStartStr) {
          return true
        }
      }
      // 规则 3：【本年度补录】模式下：
      if (replenish === 'in_year') {
        // 3.1 今天恰好是服务年度第1天 → 置灰！
        if (todayStr === winStartStr) {
          return true
        }
        // 3.2 实际剩余时间在服务年度第1天或之前（无可选的前一天） → 置灰！
        const actTime = selCard.actualUpdateTime || selCard.orderActualUpdateTime
        if (actTime) {
          const actStr = String(actTime).replace('T', ' ').slice(0, 10)
          if (actStr <= winStartStr) {
            return true
          }
        }
      }
      return false
    },
    getCardBoundaryDates() {
      const card = this.createForm.outboundType
      if (!card || !['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(card)) return {}
      let selCard = null
      if (this.createForm.orderId) {
        selCard = (this.vipCardOptions || []).find(c => c.id === this.createForm.orderId)
      } else if ((this.vipCardOptions || []).length > 0) {
        selCard = this.vipCardOptions[0]
      }
      const pad = n => String(n).padStart(2, '0')
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`

      if (!selCard) {
        selCard = { cardName: card, serviceFeeStartDate: todayStr, createTime: today }
      }
      let contractStartStr = String(selCard.serviceFeeStartDate || selCard.createTime || '').slice(0, 10)
      if (!contractStartStr) contractStartStr = this.minOutboundDateStr || todayStr

      let winStartStr = this.getCardWindowStartStr(selCard)
      if (!winStartStr) winStartStr = contractStartStr

      let actualUpdateStr = ''
      const actTime = selCard.actualUpdateTime || selCard.orderActualUpdateTime
      if (actTime) {
        actualUpdateStr = String(actTime).replace('T', ' ').slice(0, 10)
      }
      let winEndStr = ''
      if (winStartStr) {
        const parts = winStartStr.split('-').map(Number)
        if (parts.length === 3 && !parts.some(isNaN)) {
          const endDate = new Date(parts[0] + 1, parts[1] - 1, parts[2] - 1)
          winEndStr = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`
        }
      }

      // 【本年度补录】上限 (inYearUpperStr)：实际剩余时间的前一天（绝不能到当天！）
      let inYearUpperStr = ''
      if (actualUpdateStr && actualUpdateStr >= winStartStr && actualUpdateStr <= winEndStr) {
        const parts = actualUpdateStr.split('-').map(Number)
        if (parts.length === 3 && !parts.some(isNaN)) {
          const prevDate = new Date(parts[0], parts[1] - 1, parts[2] - 1)
          inYearUpperStr = `${prevDate.getFullYear()}-${pad(prevDate.getMonth() + 1)}-${pad(prevDate.getDate())}`
        }
      } else {
        const parts = todayStr.split('-').map(Number)
        if (parts.length === 3 && !parts.some(isNaN)) {
          const prevDate = new Date(parts[0], parts[1] - 1, parts[2] - 1)
          inYearUpperStr = `${prevDate.getFullYear()}-${pad(prevDate.getMonth() + 1)}-${pad(prevDate.getDate())}`
        }
      }

      // 【正常提交】起点 (normalMinDate)：
      let normalMinDate = winStartStr
      if (actualUpdateStr && actualUpdateStr >= winStartStr && actualUpdateStr <= winEndStr) {
        normalMinDate = actualUpdateStr
      } else {
        normalMinDate = todayStr < winStartStr ? winStartStr : todayStr
      }

      return { contractStartStr, winStartStr, actualUpdateStr, inYearUpperStr, normalMinDate, winEndStr, todayStr }
    },
    pickerOptions() {
      const card = this.createForm.outboundType
      if (['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(card)) {
        const bounds = this.getCardBoundaryDates
        const replenish = this.createForm.replenishType || 'normal'
        return {
          disabledDate(time) {
            const pad = n => String(n).padStart(2, '0')
            const dStr = `${time.getFullYear()}-${pad(time.getMonth() + 1)}-${pad(time.getDate())}`
            if (replenish === 'cross_year') {
              return dStr < bounds.contractStartStr || dStr >= bounds.winStartStr
            } else if (replenish === 'in_year') {
              return dStr < bounds.winStartStr || dStr > bounds.inYearUpperStr
            } else {
              return dStr < bounds.normalMinDate || dStr > bounds.winEndStr
            }
          }
        }
      }

      const minStr = this.minOutboundDateStr
      if (!minStr) return {}
      const parts = minStr.split('-').map(Number)
      if (parts.length < 3 || parts.some(isNaN)) return {}
      const minDate = new Date(parts[0], parts[1] - 1, parts[2])
      minDate.setHours(0, 0, 0, 0)
      return {
        disabledDate(time) {
          return time.getTime() < minDate.getTime()
        }
      }
    },
    timePickerOptions() {
      const card = this.createForm.outboundType
      if (['尊贵卡B', '尊贵卡C'].includes(card) && (this.createForm.replenishType || 'normal') === 'normal') {
        const bounds = this.getCardBoundaryDates
        const actStr = bounds.actualUpdateStr
        if (actStr && bounds.winStartStr <= actStr && actStr <= bounds.winEndStr && this.formDatePart === actStr) {
          let selCard = null
          if (this.createForm.orderId) {
            selCard = (this.vipCardOptions || []).find(c => c.id === this.createForm.orderId)
          } else if ((this.vipCardOptions || []).length > 0) {
            selCard = this.vipCardOptions[0]
          }
          const actTime = selCard ? (selCard.actualUpdateTime || selCard.orderActualUpdateTime) : null
          if (actTime) {
            const timeStr = String(actTime).replace('T', ' ').slice(11, 16)
            if (timeStr && timeStr.length === 5) {
              const parts = timeStr.split(':').map(Number)
              if (parts.length === 2 && !parts.some(isNaN)) {
                let nh = parts[0]
                let nm = parts[1] + 1
                if (nm >= 60) { nm = 0; nh = (nh + 1) % 24 }
                const pad = n => String(n).padStart(2, '0')
                const minTimeStr = `${pad(nh)}:${pad(nm)}`
                return {
                  selectableRange: `${minTimeStr}:00 - 23:59:59`
                }
              }
            }
          }
        }
      }
      return {
        selectableRange: '00:00:00 - 23:59:59'
      }
    },
    historyReplenishmentHintText() {
      /* 按照需求暂时注释：弹窗内不再弹出历史补录黄条提示 */
      return ''
    }
  },
  watch: {
    'createForm.outboundType'(neu) {
      // 切类型 → 清掉已选,重新拉客户档案(嵌入+档案型类型才生效)
      this.createForm.productOptKey = null
      this.createForm.productId = null
      this.createForm.productName = ''
      this.createForm.orderId = null
      if (['试用', '产品试用'].includes(neu)) {
        if (!this.createForm.applicableTypeId && this.trialTypeOptions.length > 0) {
          this.onCreateTrialTypeChange(this.trialTypeOptions[0].id)
        }
      } else if (neu === '礼品' || this.isCreateGiftCategory) {
        this.createForm.verificationType = 3
        this.createForm.verifyPhone = ''
        this.createForm.smsCode = ''
        this.createForm.attachmentUrl = ''
        this.createForm.attachmentName = ''
        this.checkCreateGiftEligibility()
      } else {
        this.createForm.applicableTypeId = null
        this.createForm.applicableTypeName = ''
        this.createForm.verificationType = 3
        this.createForm.verifyPhone = ''
        this.createForm.smsCode = ''
        this.createForm.attachmentUrl = ''
        this.createForm.attachmentName = ''
      }
      this.refreshArchiveInner()
      this.refreshVipCardOptions()
    },
    'createForm.orderId'() {
      this._applyVipCardExtra()
      this.refreshArchiveInner()
    },
    formDatePart() { this.syncOutboundDateTime() },
    formTimePart() { this.syncOutboundDateTime() }
  },
  mounted() {
    // 支持外部跳入预选状态（如个人待办点新出库未完结 → 嵌入式 outbound-management 默认筛"未完结"）
    const qs = this.$route && this.$route.query && this.$route.query.outboundStatus
    if (qs === 'uncompleted' || qs === 'completed') this.searchForm.status = qs
    this.loadList()
    this.loadOutboundTypes()
    this.loadGiftTypes()
  },
  beforeDestroy() {
    if (this.smsTimer) {
      clearInterval(this.smsTimer)
      this.smsTimer = null
    }
  },
  methods: {
    syncOutboundDateTime() {
      if (['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(this.createForm.outboundType) && this.formDatePart) {
        const bounds = this.getCardBoundaryDates
        if (bounds && bounds.winStartStr && bounds.winEndStr && bounds.winEndStr >= bounds.winStartStr) {
          const replenish = this.createForm.replenishType || 'normal'
          const dStr = this.formDatePart
          let invalid = false
          let targetD = dStr
          if (replenish === 'cross_year') {
            if (dStr < bounds.contractStartStr || dStr >= bounds.winStartStr) {
              invalid = true
              targetD = bounds.winStartStr > bounds.contractStartStr ? bounds.winStartStr : bounds.contractStartStr
            }
          } else if (replenish === 'in_year') {
            if (dStr < bounds.winStartStr || dStr > bounds.inYearUpperStr) {
              invalid = true
              targetD = bounds.winStartStr
            }
          } else {
            if (dStr < bounds.normalMinDate || dStr > bounds.winEndStr) {
              invalid = true
              targetD = bounds.normalMinDate
            }
          }
          if (invalid) {
            this.formDatePart = targetD
            return
          }
        }
      }
      if (this.formDatePart) {
        if (['尊贵卡B', '尊贵卡C'].includes(this.createForm.outboundType)) {
          let t = this.formTimePart || '00:00'
          if ((this.createForm.replenishType || 'normal') === 'normal') {
            const bounds = this.getCardBoundaryDates
            const actStr = bounds.actualUpdateStr
            if (actStr && this.formDatePart === actStr) {
              let selCard = null
              if (this.createForm.orderId) {
                selCard = (this.vipCardOptions || []).find(c => c.id === this.createForm.orderId)
              } else if ((this.vipCardOptions || []).length > 0) {
                selCard = this.vipCardOptions[0]
              }
              const actTime = selCard ? (selCard.actualUpdateTime || selCard.orderActualUpdateTime) : null
              if (actTime) {
                const rawTimeStr = String(actTime).replace('T', ' ').slice(11, 16)
                if (rawTimeStr && rawTimeStr.length === 5) {
                  const parts = rawTimeStr.split(':').map(Number)
                  if (parts.length === 2 && !parts.some(isNaN)) {
                    let nh = parts[0]
                    let nm = parts[1] + 1
                    if (nm >= 60) { nm = 0; nh = (nh + 1) % 24 }
                    const pad = n => String(n).padStart(2, '0')
                    const minTimeStr = `${pad(nh)}:${pad(nm)}`
                    if (t.slice(0, 5) < minTimeStr) {
                      t = minTimeStr
                      this.formTimePart = minTimeStr
                    }
                  }
                }
              }
            }
          }
          const timeStr = t.slice(0, 5)
          this.createForm.createTime = `${this.formDatePart} ${timeStr}:00`
        } else {
          this.createForm.createTime = `${this.formDatePart} 00:00:00`
        }
      } else {
        this.createForm.createTime = ''
      }
      if (this.$refs.createForm && this.createVisible) {
        this.$nextTick(() => {
          this.$refs.createForm && this.$refs.createForm.validateField('createTime')
        })
      }
    },
    setFormToNow() {
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      this.formDatePart = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      this.formTimePart = `${pad(d.getHours())}:${pad(d.getMinutes())}`
      this.syncOutboundDateTime()
    },
    initOutboundDateParts(datetimeStr) {
      if (!datetimeStr) {
        this.setFormToNow()
        return
      }
      const s = String(datetimeStr).replace('T', ' ')
      if (s.length >= 10) {
        this.formDatePart = s.slice(0, 10)
        if (s.length >= 16) {
          this.formTimePart = s.slice(11, 16)
        } else {
          this.formTimePart = '00:00'
        }
      }
      this.syncOutboundDateTime()
    },
    getReplenishTypeLabel(row) {
      if (!row) return '-'
      const type = row.replenishType || row.replenishMode
      const cardName = row.cardName || row.outboundType
      if (type === 'cross_year') return '跨年度补录'
      if (type === 'in_year') return '本年度内补录'
      if (type === 'normal') return cardName === '尊贵卡A' ? '本年度提交' : '正常提交'
      if (this.isHistoryReplenishment(row)) return '历史补录'
      return cardName === '尊贵卡A' ? '本年度提交' : '正常提交'
    },
    getReplenishTagType(row) {
      if (!row) return ''
      const type = row.replenishType || row.replenishMode
      if (type === 'cross_year') return 'danger'
      if (type === 'in_year') return 'warning'
      if (type === 'normal') return 'success'
      if (this.isHistoryReplenishment(row)) return 'warning'
      return 'success'
    },
    onReplenishTypeChange(val) {
      if (!['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(this.createForm.outboundType)) return
      const bounds = this.getCardBoundaryDates
      if (!bounds || !bounds.winStartStr) return
      const pad = n => String(n).padStart(2, '0')
      const curDateStr = (this.formDatePart || (this.createForm.createTime || '')).slice(0, 10)

      let targetDateStr = curDateStr
      if (val === 'cross_year') {
        if (!curDateStr || curDateStr < bounds.contractStartStr || curDateStr >= bounds.winStartStr) {
          const parts = bounds.winStartStr.split('-').map(Number)
          if (parts.length === 3 && !parts.some(isNaN)) {
            const prevDay = new Date(parts[0], parts[1] - 1, parts[2] - 1)
            targetDateStr = `${prevDay.getFullYear()}-${pad(prevDay.getMonth() + 1)}-${pad(prevDay.getDate())}`
            if (targetDateStr < bounds.contractStartStr) targetDateStr = bounds.contractStartStr
          } else {
            targetDateStr = bounds.contractStartStr
          }
        }
      } else if (val === 'in_year') {
        if (!curDateStr || curDateStr < bounds.winStartStr || curDateStr > bounds.inYearUpperStr) {
          targetDateStr = bounds.winStartStr
        }
      } else {
        const minNormalStr = bounds.normalMinDate
        if (!curDateStr || curDateStr < minNormalStr || curDateStr > bounds.winEndStr) {
          const todayStr = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`
          if (todayStr >= minNormalStr && todayStr <= bounds.winEndStr) {
            targetDateStr = todayStr
          } else {
            targetDateStr = minNormalStr
          }
        }
      }
      this.formDatePart = targetDateStr
      this.syncOutboundDateTime()
    },
    getCardWindowStartStr(card) {
      if (!card) return null
      const startDateStr = card.serviceFeeStartDate || card.createTime
      if (!startDateStr) return null
      const feeDateStr = String(startDateStr).slice(0, 10)
      const parts = feeDateStr.split('-')
      if (parts.length < 3) return null
      const startMonth = parseInt(parts[1], 10)
      const startDay = parseInt(parts[2], 10)
      const today = new Date()
      const currYear = today.getFullYear()
      let winStartYear = currYear
      const candidateDate = new Date(currYear, startMonth - 1, startDay)
      if (today < candidateDate) {
        winStartYear = currYear - 1
      }
      const pad = n => String(n).padStart(2, '0')
      return `${winStartYear}-${pad(startMonth)}-${pad(startDay)}`
    },
    isHistoryReplenishment(row) {
      if (!row || !['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(row.outboundType)) return false
      if (row.isHistoryReplenishment === true) return true
      const appTime = row.applicationTime || row.outboundDate || row.createTime
      const actualTime = row.orderActualUpdateTime || row.actualUpdateTime
      if (!appTime) return false
      const appStr = String(appTime).replace('T', ' ')
      const winStartStr = this.getCardWindowStartStr(row)
      if (winStartStr && appStr.slice(0, 10) < winStartStr) return true
      if (actualTime && row.outboundType !== '尊贵卡A' && appStr < String(actualTime).replace('T', ' ')) return true
      return false
    },
    historyReplenishmentRemarkNote(row) {
      if (!row || !this.isHistoryReplenishment(row)) return ''
      const cardName = String(row.cardName || row.outboundType || '').trim()
      const appTime = row.applicationTime || row.outboundDate || row.createTime
      const actualTime = row.orderActualUpdateTime || row.actualUpdateTime
      const isCardA = cardName.toUpperCase().includes('A')
      const winStartStr = this.getCardWindowStartStr(row)
      const appStr = String(appTime || '').replace('T', ' ')

      if (isCardA) {
        const applyFmt = String(appTime || '').slice(0, 10)
        return `申请时间（${applyFmt}）属于过去的服务年度${winStartStr ? '（早于本年度起点 ' + winStartStr + '）' : ''}，属于跨年历史补录。保存后【不会扣除】本年度的剩余可领取数量。`
      }
      const applyFmt = String(appTime || '').replace('T', ' ').slice(0, 16)
      if (actualTime && !isCardA && appStr <= String(actualTime).replace('T', ' ')) {
        const actualFmt = String(actualTime).replace('T', ' ').slice(0, 16)
        const wordUnit = cardName.toUpperCase().includes('B') ? '应该剩余额度' : '应该剩余'
        return `申请时间（${applyFmt}）早于实际剩余设置时间（${actualFmt}），属于本年度历史补录。保存后【只扣除${wordUnit}】，【不扣除实际剩余】。`
      }
      if (winStartStr && appStr.slice(0, 10) < winStartStr) {
        return `申请时间（${applyFmt}）属于过去的服务年度${winStartStr ? '（早于本年度起点 ' + winStartStr + '）' : ''}，属于跨年历史补录。保存后【不会扣除】本年度的应该剩余或实际剩余（或额度）。`
      }
      return `申请时间（${applyFmt}）属于过去的服务年度，属于跨年历史补录。保存后【不会扣除】本年度的应该剩余或实际剩余（或额度）。`
    },
    // 「超级管理员XXX代为审核」前缀切割：让前缀单独红色加粗
    proxyPrefix(t) { const m = String(t == null ? '' : t).match(/^【超级管理员.+?代为审核】/); return m ? m[0] : '' },
    proxyBody(t) { return String(t == null ? '' : t).replace(/^【超级管理员.+?代为审核】/, '') },
    isGiftOutboundType(typeName, row) {
      if (!typeName) return false
      const str = String(typeName).trim()
      if (str === '礼品' || str.startsWith('礼品·')) return true
      // 1. 动态匹配已配置的出库类型（categoryId === 1 为礼品大类）
      if (this.outboundTypes && this.outboundTypes.length) {
        const match = this.outboundTypes.find(t => t && t.name === str)
        if (match && Number(match.categoryId) === 1) return true
      }
      // 2. 检查 row 的 applicableTypeName / applicableTypeId 是否在礼品类型列表中
      if (row && (row.applicableTypeId || row.applicableTypeName)) {
        if (this.giftTypeOptions && this.giftTypeOptions.length) {
          if (this.giftTypeOptions.some(g => g.id === row.applicableTypeId || g.name === row.applicableTypeName)) {
            return true
          }
        }
      }
      // 3. 兜底常见内置礼品出库类型
      if (['损耗', '试喝', '赠送', '过期'].includes(str)) return true
      return false
    },
    vipCardLabel(category, cardSeq, applicableTypeName) {
      let stockType = null
      let rawRow = null
      if (category && typeof category === 'object') {
        rawRow = category
        stockType = category.stockType
        applicableTypeName = category.applicableTypeName
        cardSeq = category.cardSeq
        category = category.outboundType || category.cardName || category.category
      }
      if (!category) return ''
      if (category === '送存货') {
        if (stockType === '品鉴存货' || stockType === '试用存货' || applicableTypeName) {
          return (applicableTypeName && String(applicableTypeName).trim())
            ? ('品鉴存货·' + String(applicableTypeName).trim())
            : (stockType || '品鉴存货')
        }
        return stockType || '普通存货'
      }
      if (category === '产品试用' || category === '试用') {
        return (applicableTypeName && String(applicableTypeName).trim()) ? String(applicableTypeName).trim() : '试用'
      }
      // 礼品大类或礼品分类下的动态出库类型（损耗、试喝、赠送、过期或后台新增类型）
      if (this.isGiftOutboundType(category, rawRow)) {
        const subType = (applicableTypeName && String(applicableTypeName).trim()) || ''
        const cleanName = String(category).replace(/^礼品[·\s]*/, '')
        if (!cleanName || cleanName === '礼品') {
          return subType ? `礼品·${subType}` : '礼品'
        }
        return subType ? `礼品·${subType}·${cleanName}` : `礼品·${cleanName}`
      }
      const isVip = category === '尊贵卡A' || category === '尊贵卡B' || category === '尊贵卡C'
      if (!isVip) return category
      if (cardSeq == null || cardSeq === '') return category
      return category + cardSeq
    },
    // 新出库欠款：优先 row.debt；为空回落 row.totalAmount（总实付）
    getDebtForRow(row) {
      if (row.debt != null && row.debt !== '') return Number(row.debt) || 0
      if (row.totalAmount != null) return Number(row.totalAmount) || 0
      return 0
    },
    /** 欠款变更日志展示：数值 0/空 → 「未填写」；其它原样展示（保留"| 回款时间..."等附加段） */
    debtLogVal(v) {
      if (v == null || v === '') return '未填写'
      const s = String(v)
      const head = s.split('|')[0].trim()
      const n = Number(head)
      if (!isNaN(n) && n === 0) return '未填写'
      return s
    },
    // 展开行：仅新出库类型懒加载变更记录
    onExpandChange(row, expandedRows) {
      const isOpen = (expandedRows || []).some(r => r && r.id === row.id)
      if (isOpen && row.outboundType === '新出库') this.loadChangeLog(row.id)
    },
    loadChangeLog(bizId) {
      // OUTBOUND 表只有"欠款修改/状态修改"两种 action（agreementFile 走 outbound_change_log 不在此表）
      axios.get('/agreement-log', { params: { bizType: 'OUTBOUND', bizId } }).then(res => {
        const all = res.data || []
        const filtered = all.filter(l => l.action === '欠款修改' || l.action === '状态修改')
        this.$set(this.changeLogsMap, bizId, filtered)
      }).catch(() => {})
    },
    // 新出库 修改欠款：=0 自动置已完结(3)，>0 置未完结(4)
    changeDebt(row) {
      // 欠款修改仅限超级管理员
      const me = (function () { try { return JSON.parse(sessionStorage.getItem('user') || '{}') } catch (e) { return {} } })()
      const isSuper = me && (me.role === 'super_admin' || me.role === '超级管理员' || Number(me.roleId) === 1)
      if (!isSuper) {
        this.$message.warning('仅超级管理员可以修改欠款，请联系超级管理员操作')
        return
      }
      this.debtDialog = { visible: true, row }
    },
    /** 销售订单提交确认：3=已完结(后端自动清欠款)，4=未完结(欠款保持原值) */
    submitDebtStatus(newStatus) {
      const dd = this.debtDialog
      const row = dd.row
      if (!row) return
      const payload = { status: newStatus }
      if (newStatus === 3) payload.paymentDate = new Date().toISOString().slice(0, 10)
      axios.post('/outbound/' + row.id + '/status', payload).then(res => {
        const data = res.data || {}
        if (data.success) {
          if (data.changed) {
            this.$message.success(newStatus === 3 ? '已标记为已完结' : '已标记为未完结')
            this.loadList()
            this.loadChangeLog(row.id)
            try { window.dispatchEvent(new CustomEvent('outbound-debt-changed', { detail: { appId: row.id } })) } catch (e) {}
          } else {
            this.$message.info('状态未变更')
          }
          this.debtDialog.visible = false
        } else {
          this.$message.error(data.message || '更新失败')
        }
      })
    },
    // 状态显示（与审批管理一致）：礼品/试用 1=已通过、5=待出库；新出库 4=未完结
    statusLabel(type, s) {
      const isGiftOrTrial = type === '礼品' || type === '试用' || type === '产品试用'
      const m = {
        0: '审核中',
        1: isGiftOrTrial ? '待出库' : '待签字',
        2: '已驳回',
        3: '已完结',
        4: '未完结',
        5: isGiftOrTrial ? '待出库' : '已签字（待出库）'
      }
      return m[s] != null ? m[s] : s
    },
    statusTag(type, s) {
      // 新出库 + 未完结 → 红色醒目，其它沿用 info
      if (type === '新出库' && Number(s) === 4) return 'danger'
      const m = { 0: 'warning', 1: 'primary', 2: 'danger', 3: 'success', 4: 'info', 5: 'warning' }
      return m[s] || ''
    },
    loadList() {
      // 状态过滤：已完结 = 3；未完结 = 5,4；全部 = 5,3,4（沿用旧默认，过滤 0/1/2/6）
      let statusIn = '5,3,4'
      if (this.searchForm.status === 'completed') statusIn = '3'
      else if (this.searchForm.status === 'uncompleted') statusIn = '5,4'
      const params = { page: this.currentPage, size: this.pageSize, statusIn }
      // 嵌入模式：锁定该客户（保留 statusIn 等参数），仍可叠加类型/日期搜索
      if (this.embedded) params.customerId = this.embeddedCustomer.id
      if (!this.embedded && this.searchForm.customerName) params.customerName = this.searchForm.customerName
      if (this.searchForm.outboundType) params.outboundType = this.searchForm.outboundType
      if (this.searchForm.stockType) params.stockType = this.searchForm.stockType
      if (this.searchForm.giftTypeId !== '' && this.searchForm.giftTypeId !== null) params.giftTypeId = this.searchForm.giftTypeId
      if (this.searchForm.outboundDateRange && this.searchForm.outboundDateRange.length === 2) {
        params.outboundStartDate = this.searchForm.outboundDateRange[0]
        params.outboundEndDate = this.searchForm.outboundDateRange[1]
      }
      axios.get('/outbound/page', { params }).then(res => {
        this.list = (res.data && res.data.records) || []
        this.total = (res.data && res.data.total) || 0
        // 默认每行收起，按需手动点开（点开时 onExpandChange 懒加载变更记录）
        this.expandedKeys = []
      })
    },
    handleSearch() { this.currentPage = 1; this.loadList() },
    resetSearch() { this.searchForm = { customerName: '', outboundType: '', stockType: '', giftTypeId: '', outboundDateRange: null, status: '' }; this.handleSearch() },
    handleDelete(row) {
      this.$confirm('确定删除该记录？仅从列表中删除此条记录，不涉及任何库存或额度变动，删除后不可恢复。', '提示', { type: 'warning' }).then(() => {
        axios.delete('/outbound/' + row.id).then(res => {
          if (res.data && res.data.success) {
            this.$message.success('删除成功')
            this.loadList()
          } else {
            this.$message.error((res.data && res.data.message) || '删除失败')
          }
        })
      }).catch(() => {})
    },
    indexMethod(index) {
      return this.total - ((this.currentPage - 1) * this.pageSize + index)
    },
    openDispatch(row) {
      // 出库前二次确认；确认后直接出库（经办人/库管/出库时间由后端默认）
      this.$confirm('确认出库该申请？此操作将扣减公司库存且不可撤回。', '提示', { type: 'warning' })
        .then(() => {
          this.dispatching = true
          axios.post('/outbound/' + row.id + '/dispatch', {})
            .then(res => {
              if (res.data && res.data.success) {
                this.$message.success('已出库')
                this.loadList()
              } else {
                this.$message.error((res.data && res.data.message) || '出库失败')
              }
            })
            .finally(() => { this.dispatching = false })
        })
        .catch(() => {})
    },
    loadOutboundTypes() {
      axios.get('/outbound-type/list').then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        this.outboundTypes = list.filter(t => t.enabled !== 0)
      }).catch(() => { this.outboundTypes = [] })
    },
    emptyCreateForm() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const ct = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
      return {
        id: null,
        categoryId: null,
        productId: null,
        productOptKey: null,
        productName: '',
        outboundType: '',
        stockType: '普通存货',
        applicableTypeId: null,
        applicableTypeName: '',
        verificationType: 3,
        verifyPhone: '',
        smsCode: '',
        attachmentUrl: '',
        attachmentName: '',
        replenishType: 'normal',
        orderId: null,
        quantity: 1,
        batchNo: '',
        batchSplits: [{ batchNo: '', qty: 0 }],
        operator: '',
        warehouseManager: '',
        agreementFile: '',
        remark: '',
        outboundRemark: '',
        createTime: ct
      }
    },
    formatProductLabel(p) {
      if (!p) return ''
      if (p._inner) {
        if (this.createForm.outboundType === '尊贵卡C') {
          const shd = p.shouldRemaining != null ? p.shouldRemaining : (p.remaining != null ? p.remaining : 0)
          const act = p.actualRemaining != null ? p.actualRemaining : shd
          return (p.name || '') + '（应该剩余：' + shd + ' 件，实际剩余：' + act + ' 件）'
        }
        const word = this.createForm.outboundType === '送存货' ? '剩余存货' : '剩余可领取'
        return p.name + '（' + word + '：' + (p.remaining != null ? p.remaining : 0) + ' 件）'
      }
      return p.name + '（库存：' + (p.stock || 0) + ' 件）'
    },
    onCreateProductChange() {
      const key = this.createForm.productOptKey
      const opt = (this.createProductOpts || []).find(p => p.optKey === key)
      if (opt) {
        this.createForm.productId = opt.id
        this.createForm.productName = opt.name
        // 切产品就清掉旧批次并重拉该产品的入库批次列表
        this.createForm.batchNo = ''
        this.refreshBatchOpts()
      }
    },
    addCreateSplit() {
      this.createForm.batchSplits.push({ batchNo: '', qty: 0 })
    },
    removeCreateSplit(i) {
      if (this.createForm.batchSplits.length <= 1) return
      this.createForm.batchSplits.splice(i, 1)
    },
    refreshBatchOpts() {
      const pid = this.createForm.productId
      if (!pid) { this.batchOpts = []; return }
      axios.get('/inventory/batches', { params: { productId: pid } }).then(res => {
        const list = res.data || []
        // 后端已升级为 [{batchNo, remainingQuantity}]；兼容老 List<String>
        const arr = list.map(x => typeof x === 'string'
          ? { batchNo: x, remainingQuantity: 0 }
          : { batchNo: x.batchNo || x.batch || x.name || '', remainingQuantity: Number(x.remainingQuantity || 0) })
          .filter(o => o.batchNo)
        const dateKey = (s) => {
          const m = String(s).replace(/[-\/.]/g, '').match(/(\d{6,})/)
          return m ? Number(m[1]) : 0
        }
        arr.sort((a, b) => dateKey(a.batchNo) - dateKey(b.batchNo))
        this.batchOpts = arr
      }).catch(() => { this.batchOpts = [] })
    },
    onCreateVipCardChange() { /* watch createForm.orderId 已联动 */ },
    // 选中卡 → 同步剩余额度 / 实际剩余额度 / 服务期限 / 提示提示（与 VipOrder 弹窗同源同算法）
    _applyVipCardExtra() {
      this.vipDialogQuota = ''
      this.vipDialogActualQuota = ''
      this.vipDialogServiceTerm = ''
      this.serviceFeeOverdue = false
      this.serviceFeeWarning = ''
      const card = (this.vipCardOptions || []).find(c => c.id === this.createForm.orderId)
      const t = this.createForm.outboundType
      if (!card || !['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(t)) return
      if (t === '尊贵卡B') {
        const shd = card.shouldQuotaRemaining != null ? card.shouldQuotaRemaining : (card.remainingAmount != null ? card.remainingAmount : '0')
        const act = card.actualQuotaRemaining != null ? card.actualQuotaRemaining : shd
        this.vipDialogQuota = String(shd)
        this.vipDialogActualQuota = String(act)
      }
      if (t === '尊贵卡B' || t === '尊贵卡C' || t === '尊贵卡A') {
        this.vipDialogServiceTerm = this._computeServiceDueDate(card) || '—'
      }
    },
    getCardWindowStartStr(card) {
      if (!card) return null
      const startDateStr = card.serviceFeeStartDate || card.createTime
      if (!startDateStr) return null
      const feeDateStr = String(startDateStr).slice(0, 10)
      const parts = feeDateStr.split('-').map(Number)
      if (parts.length < 3 || parts.some(isNaN)) return null
      const startDate = new Date(parts[0], parts[1] - 1, parts[2])
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const pad = n => String(n).padStart(2, '0')
      if (today < startDate) {
        return `${parts[0]}-${pad(parts[1])}-${pad(parts[2])}`
      }
      let winStart = new Date(startDate)
      while (true) {
        let nextStart = new Date(winStart.getFullYear() + 1, winStart.getMonth(), winStart.getDate())
        if (today >= nextStart) {
          winStart = nextStart
        } else {
          break
        }
      }
      return `${winStart.getFullYear()}-${pad(winStart.getMonth() + 1)}-${pad(winStart.getDate())}`
    },
    getSelectedCardServiceYearWindow() {
      const t = this.createForm.outboundType
      if (!['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(t)) return ''
      const list = this.vipCardOptions || []
      const sel = list.find(c => c.id === this.createForm.orderId) || list[0]
      if (!sel || !sel.serviceFeeStartDate) return '-'
      const parts = String(sel.serviceFeeStartDate).slice(0, 10).split('-').map(Number)
      if (parts.length < 3 || parts.some(isNaN)) return '-'
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      let winStart = new Date(parts[0], parts[1] - 1, parts[2])
      const formatDate = (d) => {
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      if (today < winStart) {
        let winEnd = new Date(winStart.getFullYear() + 1, winStart.getMonth(), winStart.getDate() - 1)
        return `${formatDate(winStart)} ~ ${formatDate(winEnd)}`
      }
      while (true) {
        let nextStart = new Date(winStart.getFullYear() + 1, winStart.getMonth(), winStart.getDate())
        if (today >= nextStart) {
          winStart = nextStart
        } else {
          break
        }
      }
      let winEnd = new Date(winStart.getFullYear() + 1, winStart.getMonth(), winStart.getDate() - 1)
      return `${formatDate(winStart)} ~ ${formatDate(winEnd)}`
    },
    // 服务期限 = 当前未缴年度缴费截止日；同时设 serviceFeeOverdue / serviceFeeWarning
    _computeServiceDueDate(card) {
      if (!card || !card.serviceFeeStartDate) return ''
      const s = String(card.serviceFeeStartDate).slice(0, 10).split('-').map(Number)
      if (s.length < 3 || s.some(isNaN)) return ''
      const fm = Number(card.freeServiceMonths) > 0 ? Number(card.freeServiceMonths) : 0
      const startD = new Date(s[0], s[1] - 1, s[2])
      startD.setMonth(startD.getMonth() + fm)
      const paid = new Set(String(card.serviceFeePaidYears || '')
        .split(',').map(x => x.trim()).filter(Boolean).map(Number).filter(n => !isNaN(n)))
      let cur = new Date(startD.getTime())
      let nextDue = null
      for (let i = 0; i < 200; i++) {
        if (!paid.has(cur.getFullYear())) { nextDue = new Date(cur.getTime()); break }
        cur.setFullYear(cur.getFullYear() + 1)
      }
      if (!nextDue) return ''
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const days = Math.round((nextDue - today) / 86400000)
      if (days < 0) {
        this.serviceFeeOverdue = true
        this.serviceFeeWarning = '服务费已逾期 ' + (-days) + ' 天，请尽快缴纳服务费后可提货'
        return '已逾期 ' + (-days) + ' 天'
      }
      const warnDays = Number(card.expiryWarningDays) > 0 ? Number(card.expiryWarningDays) : 30
      if (days <= warnDays) {
        this.serviceFeeWarning = '服务费即将到期（剩 ' + days + ' 天），请尽快缴纳服务费'
      }
      const displayDue = new Date(nextDue.getTime() - 86400000)
      const yy = displayDue.getFullYear()
      const mm = String(displayDue.getMonth() + 1).padStart(2, '0')
      const dd = String(displayDue.getDate()).padStart(2, '0')
      return yy + '-' + mm + '-' + dd
    },
    // 嵌入+尊贵卡 A/B/C：拉该客户该卡的所有订单（≥2 时显示选卡下拉，=1 自动选）
    refreshVipCardOptions() {
      const t = this.createForm.outboundType
      if (!this.embedded || !this.embeddedCustomer || !this.embeddedCustomer.id
          || !['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(t)) {
        this.vipCardOptions = []
        this.createForm.orderId = null
        return
      }
      axios.get('/order/customer-vip-cards', { params: { customerId: this.embeddedCustomer.id, category: t } })
        .then(r => {
          const list = r.data || []
          this.vipCardOptions = list
          if (!list.length) { this.createForm.orderId = null; return }
          // 默认选第一张
          this.createForm.orderId = list[0].id
        })
        .catch(() => { this.vipCardOptions = []; this.createForm.orderId = null })
    },
    onStockTypeChange() {
      this.createForm.productId = null
      this.createForm.productOptKey = null
      this.createForm.productName = ''
      this.refreshArchiveInner()
    },
    // 嵌入模式下出库类型变化时，从销售订单明细直接拿"剩余存货/剩余可领取"做下拉数量(更准,与订单页同源)
    refreshArchiveInner() {
      if (!this.embedded || !this.archivePrefix || !this.embeddedCustomer || !this.embeddedCustomer.id) {
        this.createCustomerInner = []
        return
      }
      const orderParams = { page: 1, size: 100, customerId: this.embeddedCustomer.id, type: 'normal', category: this.createForm.outboundType }
      if (this.createForm.outboundType === '送存货') {
        orderParams.stockType = this.createForm.stockType || '普通存货'
      }
      axios.get('/order/page', {
        params: orderParams
      }).then(r => {
        let records = (r.data && r.data.records) || []
        const isStore = this.createForm.outboundType === '送存货'
        if (isStore) {
          let minD = ''
          records.forEach(o => {
            const dt = String(o.saleDate || o.serviceFeeStartDate || o.createTime || '').slice(0, 10)
            if (dt && (!minD || dt < minD)) minD = dt
          })
          this.songCunHuoMinDate = minD
        }
        const isVipAC = this.createForm.outboundType === '尊贵卡A' || this.createForm.outboundType === '尊贵卡C'
        // 尊贵卡 A/C：选了具体卡（orderId）时只取该卡的明细，避免下拉混入其它卡的产品
        if (isVipAC && this.createForm.orderId) {
          records = records.filter(o => o.id === this.createForm.orderId)
        }
        const agg = {} // pid -> {productId, productName, remaining, shouldRemaining, actualRemaining}
        records.forEach(o => (o.details || []).forEach(d => {
          if (d.productId == null) return
          if (Number(d.voided) === 1) return   // 已变更作废的产品：提货下拉看不到
          const left = isStore
            ? (d.remainingStock != null ? d.remainingStock : 0)
            : (d.remainingPickup != null ? d.remainingPickup : 0)
          if (left <= 0) return
          const shd = d.shouldRemaining != null ? d.shouldRemaining : left
          const act = d.actualRemaining != null ? d.actualRemaining : shd
          if (!agg[d.productId]) agg[d.productId] = { productId: d.productId, productName: d.productName, remaining: 0, shouldRemaining: 0, actualRemaining: 0 }
          agg[d.productId].remaining += Number(left) || 0
          agg[d.productId].shouldRemaining += Number(shd) || 0
          agg[d.productId].actualRemaining += Number(act) || 0
        }))
        this.createCustomerInner = Object.values(agg)
      }).catch(() => { this.createCustomerInner = [] })
    },
    agreeList(v) { return v ? String(v).split(',').map(s => s.trim()).filter(Boolean) : [] },
    full(u) {
      if (!u) return ''
      if (/^(https?:|data:)/i.test(u)) return u
      return (axios.defaults.baseURL || '').replace(/\/api\/?$/, '') + u
    },
    fileShort(u) { const p = String(u).split('/'); return p[p.length - 1] || u },
    isImage(u) { return /\.(png|jpe?g|gif|bmp|webp|svg)(\?|#|$)/i.test(String(u || '')) },
    imagePreviewList(v) { return this.agreeList(v).filter(u => this.isImage(u)).map(u => this.full(u)) },
    rmAgree(i) {
      const arr = this.agreeList(this.createForm.agreementFile)
      arr.splice(i, 1)
      this.$set(this.createForm, 'agreementFile', arr.join(','))
    },
    openAgrLog(row) {
      this.agrLogId = row.id
      this.agrLogVisible = true
    },
    openScan(row) {
      // row=null：新建弹窗内扫描；row 存在：对已存在出库单补扫
      this.scanTargetId = row ? row.id : null
      this.scanVisible = true
    },
    onAgreementScanned(url) {
      if (this.scanTargetId) {
        axios.put('/outbound/' + this.scanTargetId, { agreementFile: url }).then(res => {
          if (res.data && res.data.success !== false) {
            this.$message.success('出库明细表已保存')
            this.loadList()
          } else {
            this.$message.error((res.data && res.data.message) || '保存失败')
          }
        }).catch(() => this.$message.error('保存失败')).finally(() => { this.scanTargetId = null })
      } else {
        const arr = this.agreeList(this.createForm.agreementFile)
        arr.push(url)
        this.$set(this.createForm, 'agreementFile', arr.join(','))
      }
    },
    openCreate() {
      this.createForm = this.emptyCreateForm()
      if (this.embedded && this.embeddedCustomer) {
        const raw = (this.embeddedCustomer.contactPhone || this.embeddedCustomer.telephone || this.embeddedCustomer.phone || '').trim()
        this.createForm.verifyPhone = (raw && /^1[3-9]\d{9}$/.test(raw) && !raw.startsWith('0000')) ? raw : ''
      }
      this.initOutboundDateParts(this.createForm.createTime)
      this.outboundTypeOpts = []
      this.loadTrialTypes()
      this.loadGiftTypes()
      // 加载分类/产品/出库类型/行政管理员
      axios.get('/category/list').then(res => { this.categoryList = Array.isArray(res.data) ? res.data : [] })
      axios.get('/product/list', { params: { includeGift: true } }).then(res => { this.allProducts = Array.isArray(res.data) ? res.data : [] })
      axios.get('/user/list').then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        this.userListAll = list
        this.adminList = list.filter(u => (u.role === 'admin' || u.roleId === 2) && u.status !== 0)
      })
      this.createVisible = true
      this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.clearValidate())
    },
    loadGiftTypes() {
      axios.get('/gift-type/list', { params: { enabled: 1 } }).then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        this.allGiftTypeOptions = list
        if (!this.isCreateGiftOutbound) {
          this.giftTypeOptions = list
        }
      }).catch(() => {
        this.allGiftTypeOptions = []
        this.giftTypeOptions = []
      })
    },
    checkCreateGiftEligibility() {
      if (!this.isCreateGiftOutbound) return
      const custId = this.embedded && this.embeddedCustomer ? this.embeddedCustomer.id : null
      const custName = (this.embedded && this.embeddedCustomer ? this.embeddedCustomer.name : (this.createForm.customerName || '')).trim()
      if (!custId && !custName) {
        this.giftTypeOptions = this.allGiftTypeOptions.length ? [...this.allGiftTypeOptions] : []
        return
      }
      axios.get('/gift-recipient/customer-eligibility', {
        params: { customerId: custId || undefined, customerName: custName || undefined }
      }).then(res => {
        const data = res.data || {}
        if (data.eligible) {
          const allowed = data.allowedGiftTypes || []
          this.giftTypeOptions = allowed
          if (allowed.length === 1) {
            const single = allowed[0]
            if (this.createForm.applicableTypeId !== single.id) {
              this.createForm.applicableTypeId = single.id
              this.createForm.applicableTypeName = single.name
              this.onCreateGiftTypeChange(single.id)
            }
          } else if (this.createForm.applicableTypeId && !allowed.some(t => t.id === this.createForm.applicableTypeId)) {
            this.createForm.applicableTypeId = null
            this.createForm.applicableTypeName = ''
          }
        } else {
          this.giftTypeOptions = []
          this.createForm.applicableTypeId = null
          this.createForm.applicableTypeName = ''
          if (data.message && this.createVisible) {
            this.$message.warning(data.message)
          }
        }
      }).catch(err => {
        console.error('检查出库礼品资格失败:', err)
      })
    },
    onCreateGiftTypeChange(typeId) {
      const type = (this.giftTypeOptions || []).find(t => t.id === typeId)
      this.createForm.applicableTypeName = type ? type.name : ''
      this.createForm.productId = null
      this.createForm.productOptKey = null
      this.createForm.productName = ''
      this.createForm.batchNo = ''
    },
    loadTrialTypes() {
      axios.get('/applicable-type/list', { params: { enabled: 1 } }).then(res => {
        this.trialTypeOptions = Array.isArray(res.data) ? res.data : []
      }).catch(() => { this.trialTypeOptions = [] })
    },
    onCreateTrialTypeChange(typeId) {
      const type = (this.trialTypeOptions || []).find(t => t.id === typeId)
      if (type) {
        this.createForm.applicableTypeName = type.name
        if (type.needVerification === 1) {
          if (!this.createForm.verificationType || this.createForm.verificationType === 3) {
            this.createForm.verificationType = 1
          }
          if (!this.createForm.verifyPhone && this.embeddedCustomer) {
            const raw = (this.embeddedCustomer.contactPhone || this.embeddedCustomer.telephone || this.embeddedCustomer.phone || '').trim()
            this.createForm.verifyPhone = (raw && /^1[3-9]\d{9}$/.test(raw) && !raw.startsWith('0000')) ? raw : ''
          }
        } else {
          this.createForm.verificationType = 3
        }
      } else {
        this.createForm.applicableTypeName = ''
        this.createForm.verificationType = 3
      }
      this.createForm.productId = null
      this.createForm.productOptKey = null
      this.createForm.productName = ''
      this.createForm.batchNo = ''
    },
    sendCreateSmsCode() {
      if (!this.createForm.verifyPhone || !/^1\d{10}$/.test(this.createForm.verifyPhone.trim())) {
        this.$message.error('请输入正确的11位手机号码')
        return
      }
      const phone = this.createForm.verifyPhone.trim()
      const cid = (this.embedded && this.embeddedCustomer && this.embeddedCustomer.id) || null
      axios.get('/customer/check-phone', { params: { phone, excludeCustomerId: cid } }).then(cRes => {
        if (cRes.data && cRes.data.available === false) {
          this.$alert(cRes.data.message || `该手机号已绑定客户【${cRes.data.boundCustomerName || ''}】，严禁以此号码创建新客户，请核对姓名是否输错或换个手机号`, '无法使用该手机号', { type: 'error' })
          return
        }
        this._doSendCreateTrialSms(phone)
      }).catch(() => {
        this._doSendCreateTrialSms(phone)
      })
    },
    _doSendCreateTrialSms(phone) {
      axios.post('/sms/send-trial-code', { phone }).then(res => {
        if (res.data && res.data.success) {
          this.$message.success(res.data.message || '验证码已发送')
          this.smsCountdown = 60
          if (this.smsTimer) clearInterval(this.smsTimer)
          this.smsTimer = setInterval(() => {
            this.smsCountdown--
            if (this.smsCountdown <= 0) {
              clearInterval(this.smsTimer)
              this.smsTimer = null
            }
          }, 1000)
        } else {
          this.$message.error((res.data && res.data.message) || '发送失败')
        }
      }).catch(err => {
        this.$message.error('发送验证码失败：' + (err.message || '网络错误'))
      })
    },
    customUploadCreateVoucher(param) {
      const fd = new FormData()
      fd.append('file', param.file)
      axios.post('/file/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(res => {
        const data = res.data
        if (data && data.success !== false && data.url) {
          this.createForm.attachmentUrl = data.url
          this.createForm.attachmentName = param.file.name || data.originalFilename || data.filename || '凭证附件'
          this.$message.success('凭证上传成功')
          if (param.onSuccess) param.onSuccess(data)
        } else {
          this.$message.error((data && data.message) || '凭证上传失败')
          if (param.onError) param.onError(new Error((data && data.message) || '上传失败'))
        }
      }).catch(err => {
        this.$message.error('凭证上传失败：' + (err.message || '网络错误'))
        if (param.onError) param.onError(err)
      })
    },
    handleCreateVoucherSuccess(res) {
      if (res && res.success && res.url) {
        this.createForm.attachmentUrl = res.url
        this.createForm.attachmentName = res.originalFilename || res.filename || '凭证附件'
        this.$message.success('凭证上传成功')
      } else {
        this.$message.error((res && res.message) || '凭证上传失败')
      }
    },
    beforeCreateVoucherUpload(file) {
      const isLt20M = file.size / 1024 / 1024 < 20
      if (!isLt20M) {
        this.$message.error('凭证文件大小不能超过 20MB!')
      }
      return isLt20M
    },
    removeCreateVoucher() {
      this.createForm.attachmentUrl = ''
      this.createForm.attachmentName = ''
    },
    previewOrDownload(url) {
      if (!url) return
      if (this.isImage(url)) {
        window.open(this.full(url), '_blank')
      } else {
        const downloadApiUrl = this.full('/api/file/download?url=' + encodeURIComponent(url))
        window.open(downloadApiUrl, '_blank')
      }
    },
    onCreateCategoryChange() {
      this.createForm.productId = null
      this.createForm.productOptKey = null
      this.createForm.productName = ''
      this.createForm.outboundType = ''
      this.outboundTypeOpts = []
      if (this.isCreateGiftCategory) {
        this.createForm.batchNo = ''
      } else {
        this.createForm.applicableTypeId = null
        this.createForm.applicableTypeName = ''
      }
      if (this.createForm.categoryId) {
        axios.get('/outbound-type/list', { params: { categoryId: this.createForm.categoryId } }).then(res => {
          const list = Array.isArray(res.data) ? res.data : []
          this.outboundTypeOpts = list.filter(t => t.enabled !== 0)
        })
      }
    },
    confirmCreate() {
      if (this.isAppTimeDisabled) {
        this.$message.error('当前卡片状态或所选模式下，申请时间不可选，无法提交！')
        return
      }
      const f = this.createForm
      if (this.isCreateGiftOutbound) {
        if (!f.applicableTypeId) {
          this.$message.warning(this.giftTypeOptions.length === 0 ? '该客户未在礼品名单中配置礼品分类权限，无法提交礼品出库' : '请选择礼品类型')
          return
        }
      }
      if (this.isCreateTrialOutbound) {
        if (!f.applicableTypeId) {
          this.$message.warning('请选择试用类型')
          return
        }
        if (this.currentCreateTrialType && this.currentCreateTrialType.needVerification === 1) {
          if (f.verificationType === 1) {
            if (!f.verifyPhone || !/^1\d{10}$/.test(f.verifyPhone.trim())) {
              this.$message.warning('请输入正确的11位核验手机号')
              return
            }
            if (!f.smsCode || !f.smsCode.trim()) {
              this.$message.warning('请输入短信验证码')
              return
            }
          } else if (f.verificationType === 2) {
            if (!f.attachmentUrl) {
              this.$message.warning('请上传凭证附件')
              return
            }
          }
        }
      }
      if (['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(f.outboundType)) {
        const bounds = this.getCardBoundaryDates
        const replenish = f.replenishType || 'normal'
        const dStr = (this.formDatePart || (f.createTime || '')).slice(0, 10)
        let invalid = false
        if (replenish === 'cross_year') {
          invalid = dStr < bounds.contractStartStr || dStr >= bounds.winStartStr
        } else if (replenish === 'in_year') {
          invalid = dStr < bounds.winStartStr || dStr > bounds.inYearUpperStr
        } else {
          invalid = dStr < bounds.normalMinDate || dStr > bounds.winEndStr
        }
        if (invalid) {
          const modeName = replenish === 'cross_year' ? '跨年度补录' : (replenish === 'in_year' ? '本年度内补录' : '正常提交')
          const minDateStr = replenish === 'cross_year' ? bounds.contractStartStr : (replenish === 'in_year' ? bounds.winStartStr : bounds.normalMinDate)
          const maxDateStr = replenish === 'cross_year' ? bounds.winStartStr : (replenish === 'in_year' ? bounds.inYearUpperStr : bounds.winEndStr)
          this.$message.error(`【${modeName}】所选申请日期（${dStr}）不在允许范围内（需在 ${minDateStr} 至 ${maxDateStr} 之间），无法提交！`)
          return
        }
      }
      this.$refs.createForm.validate(valid => {
        if (!valid) return
        const f = this.createForm
        // 选了尊贵卡 A/B/C 但没选具体卡（多张时）
        if (this.embedded && ['尊贵卡A', '尊贵卡B', '尊贵卡C'].includes(f.outboundType)
            && this.vipCardOptions.length >= 2 && !f.orderId) {
          this.$message.warning('请选择具体哪张卡')
          return
        }
        // 缓冲期(1)或休眠(2)：弹提示，可强制出库
        if (this._isSuperAdmin() && ['尊贵卡B', '尊贵卡C'].includes(f.outboundType) && f.orderId) {
          const sel = (this.vipCardOptions || []).find(c => c.id === f.orderId)
          if (sel && Number(sel.dormantStatus) >= 1) {
            const reason = this._dormantReason(sel)
            if (reason) {
              const stLabel = Number(sel.dormantStatus) === 1 ? '处于休眠缓冲期' : '已休眠'
              this.$confirm('【' + (sel.label || f.outboundType) + '】' + stLabel + '：' + reason
                            + '，超管可强制出库。是否继续？', '提示',
                            { type: 'warning', confirmButtonText: '强制出库', cancelButtonText: '取消' })
                .then(() => this._doConfirmCreate(f))
                .catch(() => {})
              return
            }
          }
        }
        this._doConfirmCreate(f)
      })
    },
    _isSuperAdmin() {
      let me = {}
      try { me = JSON.parse(sessionStorage.getItem('user') || '{}') } catch (e) {}
      return me && (me.role === 'super_admin' || me.role === '超级管理员' || Number(me.roleId) === 1)
    },
    _dormantReason(card) {
      if (!card || !card.serviceFeeStartDate) return '原因未知'
      const s = String(card.serviceFeeStartDate).slice(0, 10).split('-').map(Number)
      if (s.length < 3 || s.some(isNaN)) return '原因未知'
      const fm = Number(card.freeServiceMonths) > 0 ? Number(card.freeServiceMonths) : 0
      const unlock = new Date(s[0], s[1] - 1, s[2])
      unlock.setMonth(unlock.getMonth() + fm)
      const today = new Date(); today.setHours(0, 0, 0, 0)
      if (today < unlock) {
        // 业务调整：免服务期限内可正常提货，不返回任何休眠原因
        return null
      }
      return '服务费已逾期未缴'
    },
    _doConfirmCreate(f) {
        // 批次拆分前置校验（非礼品类）：总和 = 数量；每行 qty ≤ 该批次剩余
        let detailBatchSplitJson = ''
        let firstBatchNo = f.batchNo || ''
        if (!this.isCreateGiftCategory) {
          const splits = (f.batchSplits || []).filter(s => s.batchNo && Number(s.qty) > 0)
          if (splits.length > 0) {
            const qty = Number(f.quantity) || 0
            const sum = splits.reduce((acc, x) => acc + Number(x.qty || 0), 0)
            if (sum !== qty) {
              this.$message.warning(`批次拆分合计 ${sum} ≠ 数量 ${qty}`)
              return
            }
            for (const s of splits) {
              const opt = (this.batchOpts || []).find(o => o.batchNo === s.batchNo)
              const rem = opt ? Number(opt.remainingQuantity || 0) : Number.MAX_SAFE_INTEGER
              if (Number(s.qty) > rem) {
                this.$message.warning(`批次 ${s.batchNo} 剩 ${rem}，超出选择的 ${s.qty}`)
                return
              }
            }
            firstBatchNo = splits[0].batchNo
            detailBatchSplitJson = JSON.stringify(splits.map(s => ({ batchNo: s.batchNo, qty: Number(s.qty) })))
          }
        }
        this.creating = true
        // 嵌入模式(锁定客户)：一步到位——建出库申请单(status=5)+立即 dispatch(扣库存+写流水+status=3)
        // 这样新建后既在 outbound_application 列表里显示，又写了 inventory_record 流水
        if (this.embedded && this.embeddedCustomer && this.embeddedCustomer.id) {
          const me = JSON.parse(sessionStorage.getItem('user') || '{}')
          // 提交前剥掉「存货-」前缀（只在出库管理下拉 UI 用，别污染到 提货申请/审批管理 显示）
          // 「尊贵卡-」保留：尊贵卡B 的扣数量/扣额度判定依赖前缀
          const cleanName = String(f.productName || '').replace(/^存货-/, '')
          // 「库管」选的是姓名 → 反查 adminList 拿 user.id 当 approverId（后端会写入 application.approverId，
          //  fillNames 时 approverName 即为该姓名）
          const wmUser = (this.adminList || []).find(u => (u.name || u.username) === f.warehouseManager)
          // 「经办人」输入文本 → 在全量用户里按 name/username 找匹配，作为 userId（列表"经办人"= userName 就是这个）
          //  没匹配则回退到当前后台登录账号
          const opUser = (this.userListAll || []).find(u => (u.name || u.username) === f.operator)
          const body = {
            customerId: this.embeddedCustomer.id,
            customerName: this.embeddedCustomer.name,
            userId: (opUser && opUser.id) || me.id || null,
            outboundType: f.outboundType,
            stockType: f.outboundType === '送存货' ? (f.stockType || '普通存货') : null,
            applicableTypeId: (this.isCreateTrialOutbound || this.isCreateGiftOutbound) ? f.applicableTypeId : null,
            applicableTypeName: (this.isCreateTrialOutbound || this.isCreateGiftOutbound) ? f.applicableTypeName : null,
            verificationType: this.isCreateTrialOutbound ? (f.verificationType != null ? f.verificationType : 3) : 3,
            verifyPhone: this.isCreateTrialOutbound ? (f.verifyPhone || null) : null,
            attachmentUrl: this.isCreateTrialOutbound ? (f.attachmentUrl || null) : null,
            replenishType: f.replenishType,
            orderId: f.orderId || null,
            outboundDate: f.createTime,
            applicationTime: f.createTime,
            skipApproval: true,
            skipSignature: true,
            approverId: wmUser ? wmUser.id : null,
            remark: '',
            outboundRemark: f.outboundRemark || '',
            agreementFile: f.agreementFile,
            // 把弹窗里填的生产批次随明细一起存到 outbound_application_detail.batch_no
            details: [Object.assign(
              { productId: f.productId, productName: cleanName, quantity: Number(f.quantity) || 0, batchNo: firstBatchNo },
              detailBatchSplitJson ? { batchSplit: detailBatchSplitJson } : {}
            )]
          }
          axios.post('/outbound/submit', body).then(r => {
            if (!r.data || !r.data.success) {
              this.$message.error((r.data && r.data.message) || '建出库申请失败')
              return Promise.reject(new Error('submit-fail'))
            }
            // 超管放行 B/C 免期/休眠时后端会带 warning
            if (r.data.warning) {
              this.$message({ message: r.data.warning, type: 'warning', duration: 6000 })
            }
            return axios.post('/outbound/' + r.data.id + '/dispatch', {
              operator: f.operator, warehouseManager: f.warehouseManager, dispatchDate: f.createTime
            })
          }).then(r2 => {
            if (r2 && r2.data && r2.data.success) {
              this.$message.success('新建出库成功（已扣库存+写流水）')
              this.createVisible = false
              this.loadList()
            } else if (r2) {
              this.$message.error((r2.data && r2.data.message) || '出库分发失败,申请已建但未扣库存,请到列表行点「出库」完成')
            }
          }).catch(e => {
            if (e && e.message !== 'submit-fail') this.$message.error('出库失败')
          }).finally(() => { this.creating = false })
          return
        }
        // 非嵌入(管理员独立"出库管理"页,无客户上下文): 沿用 /inventory/out 直扣库存写流水(不建申请单)
        axios.post('/inventory/out', {
          productId: f.productId,
          quantity: f.quantity,
          outboundType: f.outboundType,
          applicableTypeId: (this.isCreateTrialOutbound || this.isCreateGiftOutbound) ? f.applicableTypeId : null,
          applicableTypeName: (this.isCreateTrialOutbound || this.isCreateGiftOutbound) ? f.applicableTypeName : null,
          operator: f.operator,
          warehouseManager: f.warehouseManager,
          batchNo: f.batchNo,
          remark: f.outboundRemark || '',
          agreementFile: f.agreementFile,
          createTime: f.createTime
        }).then(res => {
          if (res.data && res.data.success) {
            this.$message.success('新建出库成功')
            this.createVisible = false
            this.loadList()
          } else {
            this.$message.error((res.data && res.data.message) || '新建失败')
          }
        }).finally(() => { this.creating = false })
    },
    // 仅前端展示：尊贵卡类出库的产品名加"尊贵卡-"前缀（已带前缀则不重复加）
    vipName(name, type) {
      if (!name) return name
      if (!(type && String(type).startsWith('尊贵卡'))) return name
      return String(name).startsWith('尊贵卡-') ? name : ('尊贵卡-' + name)
    },
    vipNames(joined, type) {
      if (!joined || !(type && String(type).startsWith('尊贵卡'))) return joined
      return String(joined).split(/[,，、]\s*/).filter(s => s !== '')
        .map(s => s.startsWith('尊贵卡-') ? s : ('尊贵卡-' + s)).join('、')
    },
    // 尊贵卡B 明细产品名集合（后端按客户尊贵卡B订单明细下发 vipBDetailNames）
    _vipBSet(row) {
      const set = {}
      const raw = row && row.vipBDetailNames
      if (raw) String(raw).split(/[,，、]\s*/).forEach(x => { const t = (x || '').trim(); if (t) set[t] = true })
      return set
    },
    // 商品名串：尊贵卡B 仅给在该客户尊贵卡B明细里的产品加前缀；A/C 整体加；其它原样；已带前缀的不重复加
    prodNames(row) {
      const joined = row && row.productNames
      const type = row && row.outboundType
      if (!joined) return joined
      if (type === '尊贵卡B') {
        const set = this._vipBSet(row)
        return String(joined).split(/[,，、]\s*/).filter(s => s !== '')
          .map(s => (set[s] && !s.startsWith('尊贵卡-')) ? ('尊贵卡-' + s) : s).join('、')
      }
      return this.vipNames(joined, type)
    },
    // 产品列带数量："产品×数量、产品×数量"；前缀复用 prodNames（按类型）；无 productNamesQty 回落纯名字
    prodNamesQty(row) {
      const raw = row && row.productNamesQty
      if (!raw) return this.prodNames(row) || '-'
      return String(raw).split(/[,，、]\s*/).filter(s => s !== '').map(seg => {
        const m = String(seg).match(/^(.*)×(\d+)$/)
        if (!m) return this.prodNames(Object.assign({}, row, { productNames: seg }))
        return this.prodNames(Object.assign({}, row, { productNames: m[1] })) + '×' + m[2]
      }).join('、')
    },
    outTypeTag(t) {
      let rawRow = null
      if (t && typeof t === 'object') {
        rawRow = t
        t = t.outboundType || t.cardName || t.category
      }
      if (t === '送存货' || t === '普通存货' || t === '品鉴存货' || t === '试用存货') return 'warning'
      if (this.isGiftOutboundType(t, rawRow)) return 'danger'
      if (t === '试用' || t === '产品试用') return 'success'
      if (t === '新出库') return 'info'
      if (t === '尊贵卡A') return 'warning' // tag-vip-a 覆盖为金
      if (t === '尊贵卡B') return 'primary'
      if (t === '尊贵卡C') return 'info'    // tag-vip-c 覆盖为紫
      return ''
    },
    outTypeClass(t) {
      if (t === '尊贵卡A') return 'tag-vip-a'
      if (t === '尊贵卡B') return 'tag-vip-b'
      if (t === '尊贵卡C') return 'tag-vip-c'
      if (t === '新出库') return 'tag-newout'
      return ''
    },
    downloadAttachment(url) {
      if (!url) return
      const downloadApiUrl = '/api/file/download?url=' + encodeURIComponent(url)
      window.open(downloadApiUrl, '_blank')
    },
    formatDate(v, len) { return v ? String(v).replace('T', ' ').slice(0, len || 19) : '' },
    exportCsv() {
      const cols = ['申请ID', '申请单号', '客户姓名', '出库类型', '产品', '数量合计', '出库日期', '提交时间', '备注']
      const rows = this.list.map(o => [
        o.id, o.applicationNo || '', o.customerName || '', this.vipCardLabel(o) || '',
        o.productNames || '', o.totalQuantity || 0,
        o.outboundDate ? String(o.outboundDate).slice(0, 10) : '',
        this.formatDate(o.createTime), o.remark || ''
      ].map(v => v == null ? '' : String(v).replace(/,/g, '，')))
      const csv = '\uFEFF' + [cols.join(','), ...rows.map(r => r.join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '出库管理_待出库_' + new Date().toISOString().slice(0, 10) + '.csv'
      link.click()
    }
  }
}
</script>

<style scoped>
.mb-4 { margin-bottom: 16px; }
.mt-4 { margin-top: 16px; text-align: right; }
.muted { color: #909399; }
.small { font-size: 12px; }

.detail-panel { padding: 14px 24px 18px; background: linear-gradient(180deg,#fafcff 0%,#f5f7fa 100%); border-top: 1px dashed #dcdfe6; }
.detail-panel-title { display: flex; align-items: center; gap: 6px; color: #303133; font-size: 13px; font-weight: 600; margin: 4px 0 12px; }
.detail-panel-title i { color: #409eff; }
.detail-card-row { background: #fff; border: 1px solid #ebeef5; border-radius: 6px; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 12px 14px; margin-bottom: 12px; }
.detail-card-body { display: grid; gap: 8px; }
.detail-card-body.cols-4 { grid-template-columns: repeat(4, 1fr); }
.dc-cell { display: flex; justify-content: flex-start; align-items: center; gap: 8px; padding: 6px 10px; background: #f8f9fb; border-radius: 4px; }
.dc-cell.w4 { grid-column: span 4; }
.dc-cell.w2 { grid-column: span 2; }
.dc-label { color: #909399; font-size: 12px; flex-shrink: 0; }
.dc-label::after { content: '：'; }
.dc-value { color: #303133; font-size: 13px; font-weight: 500; }
.report-thumbs { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.report-thumb { width: 64px; height: 64px; border-radius: 4px; border: 1px solid #ebeef5; cursor: pointer; background: #fff; }
.form-report-thumbs { margin-top: 8px; }
.report-thumb-item { position: relative; display: inline-flex; align-items: center; gap: 6px; padding: 2px 6px; }
.report-thumb-close { position: absolute; top: -6px; right: -6px; background: rgba(0,0,0,.5); color: #fff; border-radius: 50%; cursor: pointer; font-size: 14px; }
.report-file { display: inline-block; padding: 4px 8px; background: #f4f4f5; border-radius: 4px; }
/* 类型标签自定义色（金色 A / 紫色 C），与销售订单模块保持一致 */
::v-deep .el-tag.tag-vip-a {
  background-color: #fff1b8 !important;
  border-color: #faad14 !important;
  color: #874d00 !important;
  font-weight: 600 !important;
}
::v-deep .el-tag.tag-vip-c {
  background-color: #f4ecfb !important;
  border-color: #d3b4f1 !important;
  color: #722ed1 !important;
}
::v-deep .el-tag.tag-vip-b {
  background-color: #fff0f6 !important;
  border-color: #ff85c0 !important;
  color: #c41d7f !important;
}
::v-deep .el-tag.tag-newout {
  background-color: #e6fffb !important;
  border-color: #5cdbd3 !important;
  color: #08979c !important;
}
.history-replenish-dialog-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  padding: 8px 12px;
  margin: -8px 0 14px 100px;
  color: #d48800;
  font-size: 12px;
  line-height: 1.5;
}
</style>
